import { store, store$ } from '@redux/store';
import HousingProductType from 'constants/housingProductType';
import LoadingStatus from 'enums/loadingStatus';
import { googleAnalytics4Event } from 'features/googleAnalytics4';
import {
  selectFetchCountsStatusAndsCounts,
  facetsPanelSelector,
  homeFactsSelectors,
  selectLinkedPopularPillsFilters,
  selectSorting,
} from 'features/searchResultsFacets';
import { MarkerType, locationSearchParameters } from 'features/searchResultsMap';
import { FavoriteType } from 'features/userProfile';
import {
  paidAskAQuestionLocationModalSubmission$,
  paidAskAQuestionPlanModalSubmission$,
  paidAskAQuestionSpecModalSubmission$,
  paidCommunityModalSubmission$,
  basicCommunityModalSubmission$,
  paidHomeModalSubmission$,
  basicPlanModalSubmission$,
  basicSpecModalSubmission$,
  basicListingModalSubmission$,
  paidMasterPlanCommunityModalSubmission$,
  recommendedCommunitiesSubmitted$,
  summaryCardCtaClicked$,
  summaryCardLinkClicked$,
  markerUrlOpened$,
  recommendedCommunitiesSubmittedBasic$,
  modalCloseButtonClicked$,
  onPendingFavoritesChanged$,
  markerPinClicked$,
  carouselCloseButtonClicked$,
  communityMapClicked$,
  communityMapLotClicked$,
  recommendedCommunitiesShow$,
  pageWindowClick$,
  createdSaveSearchModalShown$,
  onDashboardLinkClicked$,
  onKeepSearchingButtonClicked$,
  onSavedSearchClicked$,
  summaryCardsCarouselScrolled$,
  communityMapSummaryCardClicked$,
  interactiveSitePlanBrochureSubmitted$,
  onEnterInteractiveSitePlanState$,
} from 'pages/searchResults/redux/observables';
import getClickTarget from 'pages/searchResults/services/eventTracking';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  fromEvent,
  fromEventPattern,
  map,
  merge,
  skip,
  withLatestFrom,
} from 'rxjs';
import { getLocation, getMarketSession } from 'services/googleAnalytics4EventBuilder';
import RecommendedType from 'constants/RecommendedType';
import LeadFormType from 'features/leadGenerators';
import SearchBarResultCategory from 'enums/searchResultCategory';
import CompletionSuggestionType from 'enums/completionSuggestionType';

const siteLinksEventName = 'site_links';
const outboundLinksEventName = 'outbound_links';
const buyingGuideEventsName = 'buyingguide_events';

// #region List View Section
/**
 *  ! List View Section
 *  All events related elements located in the list view should be placed in this region
 */
const listViewSection = document.querySelector('[data-list-view-element]');
const recommendationLinkDetailClick$ = fromEvent(document.body, 'click').pipe(
  filter((event) => event.target.closest('[data-recommendation-detail-link]')),
  map((event) => event.target.closest('[data-recommendation-community-item]')),
  filter(Boolean),
);
const searchResultsSection = document.querySelector('[data-search-results-content]');
const searchResultsSectionClicks$ = fromEvent(searchResultsSection, 'click');

const cardFavoriteButtonSelector = '[data-favorite-button]';

const noResultsListingLink = '[data-no-results-listings-link]';
const noResultsClearButton = '[data-no-results-clear-button]';

const paidMasterPlanCommunityCardSelector = '[data-standard-card][data-master-plan-community]';
const basicListingCardSelector = '[data-standard-card][data-basic-listing]';

const paidCommunitySummaryCardSelector = '[data-paid-community-summary-card]';
const basicCommunitySummaryCardSelector = '[data-basic-community-summary-card]';
const paidSpecSummaryCardSelector = '[data-paid-spec-summary-card]';
const basicSpecSummaryCardSelector = '[data-basic-spec-summary-card]';
const paidPlanSummaryCardSelector = '[data-paid-plan-summary-card]';
const basicPlanSummaryCardSelector = '[data-basic-plan-summary-card]';
const paidCommunityCardSelector = '[data-standard-card][data-paid-community]';
const paidPlanCardSelector = '[data-standard-card][data-paid-plan]';
const paidSpecCardSelector = '[data-standard-card][data-paid-spec]';
const basicCommunityCardSelector = '[data-standard-card][data-basic-community]';
const basicPlanCardSelector = '[data-standard-card][data-basic-plan]';
const basicSpecCardSelector = '[data-standard-card][data-basic-spec]';
const defaultMarketingStatus = 'Normal';

const paidBuilderCardSelector = '[data-builder-brand-card][data-paid-builder-brand-card]';
const basicBuilderCardSelector = '[data-builder-brand-card][data-basic-card]';
const brandNameLink = '[data-builder-brand-name-about-url]';
const viewCommunitiesLink = '[data-brand-view-communities-link]';
const viewHomesLink = '[data-brand-view-homes-link]';
const brandLogoLink = '[data-brand-logo-link]';
const reviewsArea = '[data-brand-reviews-score]';
const learnMoreLink = '[data-brand-learn-more-link]';

const paginationSection = document.querySelector('[data-pagination]');
const paginationSectionClicks$ = fromEvent(paginationSection, 'click');
const previousButtonSelector = '[data-previous]';
const nextButtonSelector = '[data-next]';
const pageNumberSelector = '[data-page]';
const pageSizeElement = document.querySelector('[data-page-size]');
const pageSizeChange$ = fromEvent(pageSizeElement, 'change');

const popularSearchPillSelector = '[data-content-popular-searches] a';
const citiesSearchSelector = '#nhs_TopResults a';
const topCommunitiesSearchSelector = '#nhs_TopCommunities a';

const searchResultFooter = document.querySelector('[data-search-result-footer]');
const searchResultFooterClicks$ = fromEvent(searchResultFooter, 'click');

const paidBoylLocationCardSelector = '[data-standard-card][data-paid-boyl-location]';
const paidBoylPlanCardSelector = '[data-standard-card][data-paid-boyl-plan]';
const paidBoylSpecCardSelector = '[data-standard-card][data-paid-boyl-spec]';
const basicBoylLocationCardSelector = '[data-standard-card][data-basic-boyl-location]';
const basicBoylPlanCardSelector = '[data-standard-card][data-basic-boyl-plan]';
const basicBoylSpecCardSelector = '[data-standard-card][data-basic-boyl-spec]';

const paidCustomLocationCardSelector = '[data-standard-card][data-paid-custom-location]';
const paidCustomPlanCardSelector = '[data-standard-card][data-paid-custom-plan]';
const paidCustomSpecCardSelector = '[data-standard-card][data-paid-custom-spec]';
const basicCustomLocationCardSelector = '[data-standard-card][data-basic-custom-location]';
const basicCustomPlanCardSelector = '[data-standard-card][data-basic-custom-plan]';
const basicCustomSpecCardSelector = '[data-standard-card][data-basic-custom-spec]';

const paidCommunityAndPaidLocationSelector = `${paidCommunityCardSelector}, ${paidBoylLocationCardSelector}, ${paidCustomLocationCardSelector}`;
const paidPlanSelector = `${paidPlanCardSelector}, ${paidBoylPlanCardSelector}, ${paidCustomPlanCardSelector}`;
const paidSpecSelector = `${paidSpecCardSelector}, ${paidBoylSpecCardSelector}, ${paidCustomSpecCardSelector}`;
const basicCommunityAndBasicLocationSelector = `${basicCommunityCardSelector}, ${basicBoylLocationCardSelector}, ${basicCustomLocationCardSelector}`;
const basicPlanSelector = `${basicPlanCardSelector}, ${basicBoylPlanCardSelector}, ${basicCustomPlanCardSelector}`;
const basicSpecSelector = `${basicSpecCardSelector}, ${basicBoylSpecCardSelector}, ${basicCustomSpecCardSelector}`;

const areaClickedPerCardElement = {
  image: 'Image Clicked',
  priceReduction: 'Price Link Clicked',
  communityName: 'Comm Link Clicked',
  planName: 'Home Link Clicked',
  specAddress: 'Home Link Clicked',
  default: 'Card Clicked',
};

// #region Regional Content
/**
 *  All events related elements located in the Regional Content section
 */

/**
 * Triggers a Google Analytics 4 event when any link in the Seo Content Area is clicked.
 */
const seoContentSection = document.querySelector('[data-regional-content]');
const seoContentSectionClicks$ = fromEvent(seoContentSection, 'click');
const seoContentSelector = '[data-regional-content] .location-table a';

const triggerSeoContentClickEvent = (element) => {
  const elementParent = element.parentElement;
  const elementTitle = elementParent.previousElementSibling;

  const customProperties = {
    click_target: 'Seo Content Area',
    search_value: `${elementTitle.innerText}: ${element.innerText}`,
    click_target1: element.href,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const seoContentClicked$ = seoContentSectionClicks$.pipe(
  filter((event) => event.target.closest(seoContentSelector)),
  map((event) => event.target),
);

seoContentClicked$.subscribe(triggerSeoContentClickEvent);

/**
 * Triggers a Google Analytics 4 event when the Read More link is clicked.
 */
const readMoreSelector = '[data-read-more-footer]';

const triggerReadMoreClickEvent = () => {
  const customProperties = {
    click_target: 'Read More Bottom SRP',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const readMoreClicked$ = seoContentSectionClicks$.pipe(filter((event) => event.target.closest(readMoreSelector)));

readMoreClicked$.subscribe(triggerReadMoreClickEvent);

/**
 * Triggers a Google Analytics 4 event when the Read Less link is clicked.
 */
const readLessSelector = '[data-read-less-footer]';

const triggerReadLessClickEvent = () => {
  const customProperties = {
    click_target: 'Read Less Bottom SRP',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const readLessClicked$ = seoContentSectionClicks$.pipe(filter((event) => event.target.closest(readLessSelector)));

readLessClicked$.subscribe(triggerReadLessClickEvent);

// #region Geo Location Content
/**
 *  All events related elements located in the Geo Location Content section
 */

/**
 * Triggers a Google Analytics 4 event when a City is clicked.
 */
const triggerCityGeoSearchClickEvent = (element) => {
  const customProperties = {
    click_target: 'SRP Cities',
    search_value: element.innerText,
    click_target1: element.href,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const cityGeoSearchClicked$ = searchResultFooterClicks$.pipe(
  filter((event) => event.target.id.includes('FooterLinkCity')),
  map((event) => event.target),
);

cityGeoSearchClicked$.subscribe(triggerCityGeoSearchClickEvent);

/**
 * Triggers a Google Analytics 4 event when a Zip is clicked.
 */

const triggerZipGeoSearchClickEvent = (element) => {
  const customProperties = {
    click_target: 'SRP Zips',
    search_value: element.innerText,
    click_target1: element.href,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const zipGeoSearchClicked$ = searchResultFooterClicks$.pipe(
  filter((event) => event.target.id.includes('FooterLinkZipCode')),
  map((event) => event.target),
);

zipGeoSearchClicked$.subscribe(triggerZipGeoSearchClickEvent);

/**
 * Triggers a Google Analytics 4 event when a County is clicked.
 */
const triggerCountyGeoSearchClickEvent = (element) => {
  const customProperties = {
    click_target: 'SRP Counties',
    search_value: element.innerText,
    click_target1: element.href,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const countyGeoSearchClicked$ = searchResultFooterClicks$.pipe(
  filter((event) => event.target.id.includes('FooterLinkCounty')),
  map((event) => event.target),
);

countyGeoSearchClicked$.subscribe(triggerCountyGeoSearchClickEvent);

// #region Snippets
/**
 *  All events related elements located in the Popular Searches and Snippets
 */

/**
 * Triggers a Google Analytics 4 event when the Popular Search pills are clicked.
 */
const triggerPopularSearchClickEvent = (element) => {
  const customProperties = {
    click_target: 'Snippet - Popular Searches',
    search_value: element.innerText,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const popularSearchClicked$ = searchResultFooterClicks$.pipe(
  filter((event) => event.target.closest(popularSearchPillSelector)),
  map((event) => event.target),
);

popularSearchClicked$.subscribe(triggerPopularSearchClickEvent);

/**
 * Triggers a Google Analytics 4 event when any Newest Community is clicked.
 */
const topCommunitiesSearchClickEvent = (element) => {
  const customProperties = {
    click_target: 'Snippet - Newest community table',
    search_value: element.innerText,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const topCommunitiesSearchClicked$ = searchResultFooterClicks$.pipe(
  filter((event) => event.target.closest(topCommunitiesSearchSelector)),
  map((event) => event.target),
);

topCommunitiesSearchClicked$.subscribe(topCommunitiesSearchClickEvent);

/**
 * Triggers a Google Analytics 4 event when any city search is clicked.
 */
const triggerCitiesSearchesClickEvent = (element) => {
  const customProperties = {
    click_target: 'Snippet - Cities',
    search_value: element.innerText,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const citySearchClicked$ = searchResultFooterClicks$.pipe(
  filter((event) => event.target.closest(citiesSearchSelector)),
  map((event) => event.target),
);

citySearchClicked$.subscribe(triggerCitiesSearchesClickEvent);

// #region Pagination
/**
 *  All events related elements located in the Pagination should be placed in this region
 */

/**
 * Triggers a Google Analytics 4 event when the previous button in the pagination is clicked.
 */
const triggerPreviousButtonClickEvent = () => {
  const customProperties = {
    click_target: 'Previous Button',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pagination_events', customProperties }));
};

const previousButtonClicked$ = paginationSectionClicks$.pipe(
  filter((event) => event.target.closest(previousButtonSelector)),
  map((event) => event.target),
);

previousButtonClicked$.subscribe(triggerPreviousButtonClickEvent);

/**
 * Triggers a Google Analytics 4 event when the next button in the pagination is clicked.
 */
const triggerNextButtonClickEvent = () => {
  const customProperties = {
    click_target: 'Next Button',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pagination_events', customProperties }));
};

const nextButtonClicked$ = paginationSectionClicks$.pipe(
  filter((event) => event.target.closest(nextButtonSelector)),
  map((event) => event.target),
);

nextButtonClicked$.subscribe(triggerNextButtonClickEvent);

/**
 * The triggerPageNumberClickEvent function is used to track the page number click on the pagination.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */
const triggerPageNumberClickEvent = (element) => {
  const customProperties = {
    click_target: 'Page Select',
    search_value: element.dataset.page,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pagination_events', customProperties }));
};

const pageNumberClicked$ = paginationSectionClicks$.pipe(
  filter((event) => event.target.closest(pageNumberSelector)),
  map((event) => event.target),
);

pageNumberClicked$.subscribe(triggerPageNumberClickEvent);

/**
 * triggerPageSizeChangeEvent: Triggers when the page size changes.
 * @param {Event} event Change event object
 */
const triggerPageSizeChangeEvent = (event) => {
  const customProperties = {
    click_target: 'Page Count Select',
    search_value: event.target.value,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pagination_events', customProperties }));
};

pageSizeChange$.subscribe(triggerPageSizeChangeEvent);

// #region Builder Directory

/**
 *  Check if the element clicked is the brand name.
 */
const isBrandNameLinkClick = (event) => {
  const anchorLink =
    event.target.closest('a[data-builder-brand-name-about-url]') ||
    event.target.closest('a[data-builder-brand-name-reviews-url]');
  return !!anchorLink;
};

/**
 *  All events related elements located in the Builder Directory tab should be placed in this region
 */
const getPaidBuilderCardProperties = (dataset) => ({
  brand_name: dataset.brandName,
  brand_id: dataset.brandId,
  is_paid: 'True',
  position: Number(dataset.index) + 1,
});

/*
Card clicks in a brand card with reviews
*/
const paidBuilderCardClickedWithReviews$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(paidBuilderCardSelector)),
  filter((event) => !event.target.closest(viewCommunitiesLink)),
  filter((event) => !event.target.closest(viewHomesLink)),
  filter((event) => !event.target.closest(brandNameLink)),
  filter((event) => !event.target.closest(brandLogoLink)),
  map((event) => ({
    cardElement: event.target.closest(paidBuilderCardSelector),
    element: event.target,
  })),
  filter(({ cardElement }) => cardElement.querySelector(reviewsArea)),
);

const triggerBuilderCardClickReviewsEvent = ({ cardElement, element }) => {
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const cardLinkElement = cardElement.querySelector('[data-builder-brand-name-reviews-url]');

  const customProperties = {
    click_target: 'Reviews BD',
    click_target1: cardAreaClicked,
    destination: cardLinkElement?.href,
    ...getPaidBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'trustbuilder_events', customProperties }));
};

paidBuilderCardClickedWithReviews$.subscribe(triggerBuilderCardClickReviewsEvent);

/*
Card clicks in a brand card without reviews
*/
const paidBuilderCardClickedArea$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(paidBuilderCardSelector)),
  filter((event) => !event.target.closest(viewCommunitiesLink)),
  filter((event) => !event.target.closest(viewHomesLink)),
  filter((event) => !event.target.closest(brandNameLink)),
  filter((event) => !event.target.closest(brandLogoLink)),
  filter((event) => !event.target.closest(reviewsArea)),
  map((event) => event.target.closest(paidBuilderCardSelector)),
  filter((card) => !card.querySelector(reviewsArea)),
);

/**
 * The triggerBuilderPaidCommunitiesClickEvent function is used to track the View Communities Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */

const triggerBuilderPaidCommunitiesClickEvent = (element) => {
  const cardElement = element.closest(paidBuilderCardSelector);
  const cardDataset = cardElement.dataset;
  const cardLinkElement = cardElement.querySelector(viewCommunitiesLink);

  const customProperties = {
    click_target: 'View Communities BD',
    destination: cardLinkElement?.href,
    ...getPaidBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

paidBuilderCardClickedArea$.subscribe(triggerBuilderPaidCommunitiesClickEvent);

const paidCommunityLinkClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(viewCommunitiesLink)),
  map((event) => event.target),
);

paidCommunityLinkClicked$.subscribe(triggerBuilderPaidCommunitiesClickEvent);

/**
 * The triggerBuilderPaidLogoClickEvent function is used to track the Builder Logo Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */

const triggerBuilderPaidLogoClickEvent = (element) => {
  const cardElement = element.closest(paidBuilderCardSelector);
  const cardDataset = cardElement.dataset;
  const cardLinkElement = cardElement.querySelector(brandLogoLink);

  const customProperties = {
    click_target: 'Learn more about builder',
    destination: cardLinkElement?.href,
    ...getPaidBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'showcase_events', customProperties }));
};

const paidBuilderLogoClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(brandLogoLink)),
  filter((event) => event.target.closest(paidBuilderCardSelector)),
  map((event) => event.target),
);

paidBuilderLogoClicked$.subscribe(triggerBuilderPaidLogoClickEvent);

const paidBuilderNameClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => isBrandNameLinkClick(event)),
  map((event) => event.target),
);

paidBuilderNameClicked$.subscribe(triggerBuilderPaidLogoClickEvent);

paidBuilderCardClickedArea$.subscribe(triggerBuilderPaidLogoClickEvent);

const triggerBuilderCardClickShowcaseEvent = (cardElement) => {
  const cardDataset = cardElement.dataset;
  const cardLinkElement = cardElement.querySelector(brandLogoLink);

  const customProperties = {
    click_target: 'Learn more about builder',
    destination: cardLinkElement?.href,
    ...getPaidBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'showcase_events', customProperties }));
};

paidBuilderCardClickedArea$.subscribe(triggerBuilderCardClickShowcaseEvent);

/**
 * The triggerBuilderPaidLinkClickEvent function is used to track the View Homes Link Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */

const triggerBuilderPaidLinkClickEvent = (element) => {
  const cardElement = element.closest(paidBuilderCardSelector);
  const cardDataset = cardElement.dataset;
  const cardLinkElement = cardElement.querySelector(viewHomesLink);

  const customProperties = {
    click_target: 'View Homes BD',
    destination: cardLinkElement?.href,
    ...getPaidBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_events', customProperties }));
};

const paidBuilderLinkClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(viewHomesLink)),
  map((event) => event.target),
);

paidBuilderLinkClicked$.subscribe(triggerBuilderPaidLinkClickEvent);

const paidBuilderClickedCard$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(paidBuilderCardSelector)),
  filter((event) => !event.target.closest(learnMoreLink)),
  map((event) => event.target),
);

const triggerBuilderPaidCardClickEvent = (element) => {
  const cardElement = element.closest(paidBuilderCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;

  const customProperties = {
    click_target: 'Card Clicked',
    click_target1: cardAreaClicked,
    ...getPaidBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'paid_builder_area', customProperties }));
};

paidBuilderClickedCard$.subscribe(triggerBuilderPaidCardClickEvent);

// Basic Builder Card Events Below

const getBasicBuilderCardProperties = (dataset) => ({
  brand_name: dataset.brandName,
  brand_id: dataset.brandId,
  is_paid: 'False',
  position: Number(dataset.index) + 1,
});

const basicBuilderClickedCard$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(basicBuilderCardSelector)),
  filter((event) => !event.target.closest(learnMoreLink)),
  map((event) => event.target),
);

const triggerBuilderBasicCardClickEvent = (element) => {
  const cardElement = element.closest(basicBuilderCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;

  const customProperties = {
    click_target: 'Card Clicked',
    click_target1: cardAreaClicked,
    ...getBasicBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'basic_builder_area', customProperties }));
};

basicBuilderClickedCard$.subscribe(triggerBuilderBasicCardClickEvent);

/**
 * The triggerBuilderBasicLinkClickEvent function is used to track the Learn More Link Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */

const triggerBuilderBasicLinkClickEvent = (element) => {
  const cardElement = element.closest(basicBuilderCardSelector);
  const cardDataset = cardElement.dataset;
  const cardLinkElement = cardElement.querySelector(learnMoreLink);

  const customProperties = {
    click_target: cardLinkElement.innerText,
    destination: cardLinkElement?.href,
    ...getBasicBuilderCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'basic_builder_area', customProperties }));
};

const basicBuilderLinkClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.closest(learnMoreLink)),
  filter((event) => event.target.closest(basicBuilderCardSelector)),
  map((event) => event.target),
);

basicBuilderLinkClicked$.subscribe(triggerBuilderBasicLinkClickEvent);

/**
 * Checks if a price is valid and not equal to zero.
 * @param price - is a variable that represents the price of a listing
 */
const isPriced = (price) => price && price !== '0';

/**
 * Calculate the position based on an index.
 * @param index - is a variable that represents the index of the card in the SRP
 */
const getPosition = (index) => (index ? Number(index) + 1 : 0);

const getPaidCommunityCardProperties = (dataset) => ({
  builder_id: dataset.builderId,
  builder_name: dataset.brandName,
  community_id: dataset.communityId,
  community_name: dataset.communityName,
  community_status: dataset.marketingStatus || defaultMarketingStatus,
  is_paid: 'True',
  is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  max_price: dataset.priceHigh,
  min_price: dataset.priceLow,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  trustbuilder_reviews: dataset.hasBuilderReviews,
  zip_code: dataset.zip,
});

const getBasicCommunityCardProperties = (dataset) => ({
  builder_name: dataset.brandName,
  community_id: dataset.communityId,
  community_name: dataset.communityName,
  community_status: dataset.marketingStatus || defaultMarketingStatus,
  is_paid: 'False',
  is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  max_price: dataset.priceHigh,
  min_price: dataset.priceLow,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  trustbuilder_reviews: dataset.hasBuilderReviews,
});

const getPaidPlanCardProperties = (dataset) => ({
  builder_id: dataset.builderId,
  builder_name: dataset.brandName,
  community_id: dataset.communityId,
  community_name: dataset.communityName,
  home_status: dataset.homeStatus,
  is_paid: 'True',
  is_plan: 'True',
  is_priced: isPriced(dataset.price) ? 'True' : 'False',
  is_spec: 'False',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  min_price: dataset.price,
  plan_id: dataset.planId,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  trustbuilder_reviews: dataset.hasBuilderReviews,
  zip_code: dataset.zip,
});

const getPaidSpecCardProperties = (dataset) => ({
  builder_id: dataset.builderId,
  builder_name: dataset.brandName,
  community_id: dataset.communityId,
  community_name: dataset.communityName,
  home_status: dataset.homeStatus,
  is_paid: 'True',
  is_plan: 'False',
  is_priced: isPriced(dataset.price) ? 'True' : 'False',
  is_spec: 'True',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  min_price: dataset.price,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  spec_id: dataset.specId,
  trustbuilder_reviews: dataset.hasBuilderReviews,
  zip_code: dataset.zip,
});

const getBasicPlanCardProperties = (dataset) => ({
  builder_name: dataset.brandName,
  community_id: dataset.communityId,
  community_name: dataset.communityName,
  home_status: dataset.homeStatus,
  is_paid: 'False',
  is_plan: 'True',
  is_priced: isPriced(dataset.price) ? 'True' : 'False',
  is_spec: 'False',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  min_price: dataset.price,
  plan_id: dataset.planId,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  trustbuilder_reviews: dataset.hasBuilderReviews,
});

const getBasicSpecCardProperties = (dataset) => ({
  builder_name: dataset.brandName,
  community_id: dataset.communityId,
  community_name: dataset.communityName,
  home_status: dataset.homeStatus,
  is_paid: 'False',
  is_plan: 'False',
  is_priced: isPriced(dataset.price) ? 'True' : 'False',
  is_spec: 'True',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  min_price: dataset.price,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  spec_id: dataset.specId,
  trustbuilder_reviews: dataset.hasBuilderReviews,
});

const getBasicListingCardProperties = (dataset) => ({
  builder_name: dataset.brandName,
  is_paid: 'False',
  is_priced: isPriced(dataset.price) ? 'True' : 'False',
  is_spotlight: dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
  listing_id: dataset.id,
  location: getLocation(dataset.city, dataset.stateName),
  market_id: dataset.marketId,
  market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
  market_session: getMarketSession(dataset.marketId, dataset.marketName, dataset.stateAbbreviation),
  min_price: dataset.price,
  position: getPosition(dataset.index),
  price_reduction: dataset.priceReductionLabel,
  trustbuilder_reviews: dataset.hasBuilderReviews,
});

const isNoResultsPage = () => !!listViewSection.querySelector('[data-no-result="true"]');

const getClickTargetText = (isMapCard = false) => {
  const srpClickTargetText = isMapCard ? 'SRP Map Card' : 'SRP Card';
  return isNoResultsPage() ? 'Zero Results Card' : srpClickTargetText;
};

const triggerNoResultImpressionEvent = () =>
  store.dispatch(googleAnalytics4Event({ eventName: 'zero_results', customProperties: {} }));

/**
 *  Observable checking the changes in the listing counts selector,
 * checks if the ajax request is succes and the gets the counts value.
 */
const countsChange$ = store$.pipe(
  map(selectFetchCountsStatusAndsCounts),
  distinctUntilKeyChanged('resultCountsStatus'),
  filter((counts) => counts.resultCountsStatus !== LoadingStatus.Loading),
  filter((counts) => counts.listingCounts === 0),
);

countsChange$.subscribe(triggerNoResultImpressionEvent);

const noResultsListingLinkClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(isNoResultsPage),
  filter(({ clickTarget }) => clickTarget.closest(noResultsListingLink)),
  map(({ clickTarget }) => clickTarget),
);

const triggerNoResultsListingLink = (element) => {
  const anchorElement = element.closest(noResultsListingLink);
  const anchorLink = anchorElement?.href;
  const anchorText = anchorElement?.text;

  const customProperties = {
    click_target: anchorText,
    destination: anchorLink,
    is_zero_results: true,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'site_links', customProperties }));
};

noResultsListingLinkClickedCard$.subscribe(triggerNoResultsListingLink);

const noResultsClearFilterButtonClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(isNoResultsPage),
  filter(({ clickTarget }) => clickTarget.closest(noResultsClearButton)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  filter(({ clickTarget }) => !clickTarget.closest(cardFavoriteButtonSelector)),
  map(({ clickTarget }) => clickTarget),
);

const triggerNoResultsClearFiltersButton = (element) => {
  const cardElement = element.closest(noResultsClearButton);
  const anchorText = cardElement?.text;

  const customProperties = {
    click_target: anchorText,
    filter_name: 'Advanced Search',
    is_zero_results: true,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};

noResultsClearFilterButtonClickedCard$.subscribe(triggerNoResultsClearFiltersButton);

const paidCommunityClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(paidCommunityCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  filter(({ clickTarget }) => !clickTarget.closest(cardFavoriteButtonSelector)),
  map(({ clickTarget }) => clickTarget),
);

const triggerCommunityCardClickEvent = (element) => {
  const cardElement = element.closest(paidCommunityCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getPaidCommunityCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

paidCommunityClickedCard$.subscribe(triggerCommunityCardClickEvent);

const paidCommunityClickedSummaryCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(paidCommunitySummaryCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  map(({ clickTarget }) => clickTarget),
);

const triggerCommunitySummaryCardClickEvent = (element) => {
  const cardElement = element.closest(paidCommunitySummaryCardSelector);
  const cardDataset = cardElement.dataset;
  const anchorLink = cardDataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: areaClickedPerCardElement.default,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getPaidCommunityCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
};

paidCommunityClickedSummaryCard$.subscribe(triggerCommunitySummaryCardClickEvent);

const basicCommunityClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => clickEvent.target),
  filter((clickTarget) => clickTarget.closest(basicCommunityCardSelector)),
  filter((clickTarget) => !clickTarget.dataset?.leadGeneratorOpener),
  filter((clickTarget) => !clickTarget.closest(cardFavoriteButtonSelector)),
);

const triggerBasicCommunityCardClickEvent = (element) => {
  const cardElement = element.closest(basicCommunityCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicCommunityCardProperties(cardDataset),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

basicCommunityClickedCard$.subscribe(triggerBasicCommunityCardClickEvent);

const basicCommunityClickedSummaryCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(basicCommunitySummaryCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  map(({ clickTarget }) => clickTarget),
);

const triggerBasicCommunitySummaryCardClickEvent = (element) => {
  const cardElement = element.closest(basicCommunitySummaryCardSelector);
  const cardDataset = cardElement.dataset;
  const anchorLink = cardDataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: areaClickedPerCardElement.default,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicCommunityCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
};

basicCommunityClickedSummaryCard$.subscribe(triggerBasicCommunitySummaryCardClickEvent);

const paidPlanClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(paidPlanCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  filter(({ clickTarget }) => !clickTarget.closest(cardFavoriteButtonSelector)),
  map(({ clickTarget }) => clickTarget),
);

const triggerPlanCardClickEvent = (element) => {
  const cardElement = element.closest(paidPlanCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getPaidPlanCardProperties(cardDataset),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

paidPlanClickedCard$.subscribe(triggerPlanCardClickEvent);

const paidPlanClickedSummaryCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(paidPlanSummaryCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  map(({ clickTarget }) => clickTarget),
);

const triggerPlanSummaryCardClickEvent = (element) => {
  const cardElement = element.closest(paidPlanSummaryCardSelector);
  const cardDataset = cardElement.dataset;
  const anchorLink = cardDataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: areaClickedPerCardElement.default,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getPaidPlanCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
};

paidPlanClickedSummaryCard$.subscribe(triggerPlanSummaryCardClickEvent);

const basicPlanClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => clickEvent.target),
  filter((clickTarget) => clickTarget.closest(basicPlanCardSelector)),
  filter((clickTarget) => !clickTarget.dataset?.leadGeneratorOpener),
  filter((clickTarget) => !clickTarget.closest(cardFavoriteButtonSelector)),
);

const triggerBasicPlanCardClickEvent = (element) => {
  const cardElement = element.closest(basicPlanCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicPlanCardProperties(cardDataset),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

basicPlanClickedCard$.subscribe(triggerBasicPlanCardClickEvent);

const basicPlanClickedSummaryCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(basicPlanSummaryCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  map(({ clickTarget }) => clickTarget),
);

const triggerBasicPlanSummaryCardClickEvent = (element) => {
  const cardElement = element.closest(basicPlanSummaryCardSelector);
  const cardDataset = cardElement.dataset;
  const anchorLink = cardDataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: areaClickedPerCardElement.default,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicPlanCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
};

basicPlanClickedSummaryCard$.subscribe(triggerBasicPlanSummaryCardClickEvent);

const basicListingClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => clickEvent.target),
  filter((clickTarget) => clickTarget.closest(basicListingCardSelector)),
  filter((clickTarget) => !clickTarget.dataset?.leadGeneratorOpener),
  filter((clickTarget) => !clickTarget.closest(cardFavoriteButtonSelector)),
);

const triggerBasicListingCardClickEvent = (element) => {
  const cardElement = element.closest(basicListingCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicListingCardProperties(cardDataset),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

basicListingClickedCard$.subscribe(triggerBasicListingCardClickEvent);

const paidSpecClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(paidSpecCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  filter(({ clickTarget }) => !clickTarget.closest(cardFavoriteButtonSelector)),
  map(({ clickTarget }) => clickTarget),
);

const triggerSpecCardClickEvent = (element) => {
  const cardElement = element.closest(paidSpecCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getPaidSpecCardProperties(cardDataset),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

paidSpecClickedCard$.subscribe(triggerSpecCardClickEvent);

const paidSpecClickedSummaryCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(paidSpecSummaryCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  map(({ clickTarget }) => clickTarget),
);

const triggerSpecSummaryCardClickEvent = (element) => {
  const cardElement = element.closest(paidSpecSummaryCardSelector);
  const cardDataset = cardElement.dataset;
  const anchorLink = cardDataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: areaClickedPerCardElement.default,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getPaidSpecCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
};

paidSpecClickedSummaryCard$.subscribe(triggerSpecSummaryCardClickEvent);

const basicSpecClickedCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => clickEvent.target),
  filter((clickTarget) => clickTarget.closest(basicSpecCardSelector)),
  filter((clickTarget) => !clickTarget.dataset?.leadGeneratorOpener),
  filter((clickTarget) => !clickTarget.closest(cardFavoriteButtonSelector)),
);

const triggerBasicSpecCardClickEvent = (element) => {
  const cardElement = element.closest(basicSpecCardSelector);
  const cardDataset = cardElement.dataset;
  const elementName = element.dataset.cardElement;
  const cardAreaClicked = areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
  const anchorLink = cardElement.querySelector('[data-url]')?.dataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: cardAreaClicked,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicSpecCardProperties(cardDataset),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

basicSpecClickedCard$.subscribe(triggerBasicSpecCardClickEvent);

const basicSpecClickedSummaryCard$ = searchResultsSectionClicks$.pipe(
  map((clickEvent) => ({
    clickTarget: clickEvent.target,
  })),
  filter(({ clickTarget }) => clickTarget.closest(basicSpecSummaryCardSelector)),
  filter(({ clickTarget }) => !clickTarget.dataset?.leadGeneratorOpener),
  map(({ clickTarget }) => clickTarget),
);

const triggerBasicSpecSummaryCardClickEvent = (element) => {
  const cardElement = element.closest(basicSpecSummaryCardSelector);
  const cardDataset = cardElement.dataset;
  const anchorLink = cardDataset.url;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: areaClickedPerCardElement.default,
    destination: anchorLink,
    is_zero_results: isNoResults,
    ...getBasicSpecCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
};

basicSpecClickedSummaryCard$.subscribe(triggerBasicSpecSummaryCardClickEvent);

/* Paid Community Favorited: User not logged in */
const triggerCommunityFavoritedEvent = (payload) => {
  const itemId = payload?.listingInformation?.id;
  const cardElement = document.querySelector(`[data-id="${itemId}"]`);
  const cardDataset = cardElement?.dataset ?? {};
  const isNoResults = isNoResultsPage();

  if (!cardElement) {
    return;
  }
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const getCommunityCardProperties =
    cardDataset.isBasic === 'true'
      ? getBasicCommunityCardProperties(cardDataset)
      : getPaidCommunityCardProperties(cardDataset);

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 4,
    is_zero_results: isNoResults,
    ...getCommunityCardProperties,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'saved', customProperties }));
};

onPendingFavoritesChanged$
  .pipe(filter((favoritedInformation) => favoritedInformation?.type === FavoriteType.Community.toString()))
  .subscribe(triggerCommunityFavoritedEvent);

/* Paid Plan Favorited: User not logged in */
const triggerPlanFavoritedEvent = (payload) => {
  const itemId = payload?.listingInformation?.id;
  const cardElement = document.querySelector(`[data-id="${itemId}"]`);
  const cardDataset = cardElement?.dataset ?? {};
  const isNoResults = isNoResultsPage();

  if (!cardElement) {
    return;
  }
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const getPlanCardProperties =
    cardDataset.isBasic === 'true' ? getBasicPlanCardProperties(cardDataset) : getPaidPlanCardProperties(cardDataset);

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 4,
    is_zero_results: isNoResults,
    ...getPlanCardProperties,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'saved', customProperties }));
};

onPendingFavoritesChanged$
  .pipe(filter((favoritedInformation) => favoritedInformation?.type === FavoriteType.Plan.toString()))
  .subscribe(triggerPlanFavoritedEvent);

/* Paid Spec Favorited: User not logged in */
const triggerSpecFavoritedEvent = (payload) => {
  const itemId = payload?.listingInformation?.id;
  const cardElement = document.querySelector(`[data-id="${itemId}"]`);
  const cardDataset = cardElement?.dataset ?? {};
  const isNoResults = isNoResultsPage();

  if (!cardElement) {
    return;
  }
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const getSpecCardProperties =
    cardDataset.isBasic === 'true' ? getBasicSpecCardProperties(cardDataset) : getPaidSpecCardProperties(cardDataset);

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 4,
    is_zero_results: isNoResults,
    ...getSpecCardProperties,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'saved', customProperties }));
};

onPendingFavoritesChanged$
  .pipe(filter((favoritedInformation) => favoritedInformation?.type === FavoriteType.Spec.toString()))
  .subscribe(triggerSpecFavoritedEvent);

function triggerRecommendationLinkDetailEvent(item) {
  const community = JSON.parse(item.querySelector('[data-recommended-checkbox]').value);
  const customProperties = {
    click_target: getClickTargetText(),
    click_target1: 'Comm Link Clicked',
    community_id: community.CommunityId,
    community_name: community.CommunityName,
    community_status: community.MarketingStatusLabel || defaultMarketingStatus,
    is_paid: 'True',
    is_priced: isPriced(community.PriceLow) || isPriced(community.PriceHigh) ? 'True' : 'False',
    is_spotlight: item.dataset.wasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    location: getLocation(community.City, community.StateName),
    market_id: community.MarketId,
    market_name: community.MarketName,
    market_session: getMarketSession(community.MarketId, community.MarketName, community.StateAbbreviation),
    max_price: community.PriceHigh,
    min_price: community.PriceLow,
    trustbuilder_reviews: community.HasBuilderReviews,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
}

recommendationLinkDetailClick$.subscribe(triggerRecommendationLinkDetailEvent);

// ! Community Lead Button Click Event //

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidCommunityLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidCommunityAndPaidLocationSelector)),
  map((event) => event.target),
);

/**
 * The triggerCommunityPaidLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param action Get the target element of the event
 */
const triggerCommunityPaidLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidCommunityAndPaidLocationSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidCommunityCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

paidCommunityLeadButtonClicked$.subscribe(triggerCommunityPaidLeadButtonClickEvent);

const paidMasterPlanCommunityLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidMasterPlanCommunityCardSelector)),
  map((event) => event.target),
);

/**
 * The triggerMasterPlanCommunityPaidLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param action Get the target element of the event
 */
const triggerMasterPlanCommunityPaidLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidMasterPlanCommunityCardSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    ...getPaidCommunityCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

paidMasterPlanCommunityLeadButtonClicked$.subscribe(triggerMasterPlanCommunityPaidLeadButtonClickEvent);

const triggerBasicCommunityLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicCommunityAndBasicLocationSelector);
  const cardDataset = cardElement.dataset;
  const buttonDataset = element.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getBasicCommunityCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

const basicCommunityLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicCommunityAndBasicLocationSelector)),
  map((event) => event.target),
);

basicCommunityLeadButtonClicked$.subscribe(triggerBasicCommunityLeadButtonClickEvent);

// ! Home (Spec/Plan) Lead Button Click Event //

/**
 * The triggerPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */
const triggerPaidPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidPlanSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidPlanCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidPlanLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidPlanSelector)),
  map((event) => event.target),
);

paidPlanLeadButtonClicked$.subscribe(triggerPaidPlanLeadButtonClickEvent);

/**
 * The triggerPlanLeadButtonClickEvent function is used to track the Lead Button Click.
 * This will gather the custom properties and dispatch the GA4 event
 * @param element Get the target element of the event
 */
const triggerPaidSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(paidSpecSelector);
  const buttonDataset = element.dataset;
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidSpecCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

// observable created when there is a click in the list view.
// It will filter the actions to identify the ones performed in the CTA button.
const paidSpecLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(paidSpecSelector)),
  map((event) => event.target),
);

paidSpecLeadButtonClicked$.subscribe(triggerPaidSpecLeadButtonClickEvent);

const triggerBasicPlanLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicPlanSelector);
  const cardDataset = cardElement.dataset;
  const buttonDataset = element.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getBasicPlanCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

const basicPlanLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicPlanSelector)),
  map((event) => event.target),
);

basicPlanLeadButtonClicked$.subscribe(triggerBasicPlanLeadButtonClickEvent);

const triggerBasicSpecLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicSpecSelector);
  const cardDataset = cardElement.dataset;
  const buttonDataset = element.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getBasicSpecCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

const basicSpecLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicSpecSelector)),
  map((event) => event.target),
);

basicSpecLeadButtonClicked$.subscribe(triggerBasicSpecLeadButtonClickEvent);

const triggerBasicListingLeadButtonClickEvent = (element) => {
  const cardElement = element.closest(basicListingCardSelector);
  const cardDataset = cardElement.dataset;
  const buttonDataset = element.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    button_name: buttonDataset.buttonAction,
    is_zero_results: isNoResults,
    ...getBasicListingCardProperties(cardDataset),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
};

const basicListingLeadButtonClicked$ = searchResultsSectionClicks$.pipe(
  filter((event) => event.target.dataset.leadGeneratorOpener),
  filter((event) => event.target.closest(basicListingCardSelector)),
  map((event) => event.target),
);

basicListingLeadButtonClicked$.subscribe(triggerBasicListingLeadButtonClickEvent);

// #endregion List View Section

// #region Brochure Modal
/**
 *  ! Brochure Modal Section
 *  All events related elements located in brochure modal should be placed in this region
 */

const detailAnchorClicked$ = fromEvent(document.body, 'detailAnchorClicked').pipe(map((event) => event.detail));

/**
 * trackRealtorQuestionAnswerEvent: Tracks the sorted event for the realtor dropdown.
 */
function getRealtorQuestionAnswerText() {
  const realtorSelect = document.querySelector('[name="realtorQuestion"]');
  let realtorQuestionAnswer = '';
  if (realtorSelect && realtorSelect.selectedIndex > 0) {
    realtorQuestionAnswer = realtorSelect.options[realtorSelect.selectedIndex].text;
  }
  return realtorQuestionAnswer;
}

/**
 * triggerPaidCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidCommunityModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidCommunityCardProperties(modalInformation.listingInformation),
    realtor_question: getRealtorQuestionAnswerText(),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'generate_lead', customProperties }));
}

paidCommunityModalSubmission$.subscribe(triggerPaidCommunityModalSubmitted);
paidMasterPlanCommunityModalSubmission$.subscribe(triggerPaidCommunityModalSubmitted);
paidAskAQuestionLocationModalSubmission$.subscribe(triggerPaidCommunityModalSubmitted);

/**
 * Handles the event when a paid community modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handlePaidCommunityModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getPaidCommunityCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Handles the event when a basic community modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handleBasicCommunityModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getBasicCommunityCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a paid community modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.Community().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic !== 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedPaid().getName()),
  )
  .subscribe(handlePaidCommunityModalFormClosed);

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a basic community modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.Community().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic === 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedBasic().getName()),
  )
  .subscribe(handleBasicCommunityModalFormClosed);

/**
 * Handles the event when a paid spec modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handlePaidSpecModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getPaidSpecCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Handles the event when a basic spec modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handleBasicSpecModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getBasicSpecCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a paid spec modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.Spec().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic !== 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedPaid().getName()),
  )
  .subscribe(handlePaidSpecModalFormClosed);

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a basic spec modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.Spec().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic === 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedBasic().getName()),
  )
  .subscribe(handleBasicSpecModalFormClosed);

/**
 * Handles the event when a paid plan modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handlePaidPlanModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getPaidPlanCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Handles the event when a basic plan modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handleBasicPlanModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getBasicPlanCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a paid plan modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.Plan().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic !== 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedPaid().getName()),
  )
  .subscribe(handlePaidPlanModalFormClosed);

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a basic plan modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.Plan().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic === 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedBasic().getName()),
  )
  .subscribe(handleBasicPlanModalFormClosed);

/**
 * Handles the event when a basic community modal form is closed.
 * @param {Object} modalInformation - Information about the modal that was closed.
 */
function handleBasicListingModalFormClosed(event) {
  const { modalInformation } = event.detail;
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    is_zero_results: isNoResults,
    click_target: clickTarget,
    ...getBasicListingCardProperties(modalInformation.listingInformation),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

/**
 * Subscribes to the `modalCloseButtonClicked$` observable and handles the closing of a paid community modal form.
 *
 */
modalCloseButtonClicked$
  .pipe(
    filter((event) => event.detail?.modalInformation?.type === HousingProductType.BasicListing().getName()),
    filter((event) => event.detail?.modalInformation?.listingInformation?.isBasic === 'true'),
    filter((event) => event.detail?.modalInformation?.name !== RecommendedType.RecommendedBasic().getName()),
  )
  .subscribe(handleBasicListingModalFormClosed);

/**
 * triggerBasicCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following form:
 * - Learn More
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */

function triggerBasicCommunityModalSubmitted(modalInformation) {
  const cardElement = document.querySelector(`[data-community-id="${modalInformation.id}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...getBasicCommunityCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'basic_lead_submitted', customProperties }));
}

basicCommunityModalSubmission$.subscribe(triggerBasicCommunityModalSubmitted);

/**
 * Triggers the events for the recommended basic communities that were selected.
 * @param {Object} modalInformation Information related to the modal.
 */
function triggerRecommendedBasicCommunitySubmittedEvents(modalInformation) {
  const { selectedRecommendedCommunities, listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  selectedRecommendedCommunities.forEach((community) => {
    const customProperties = {
      button_name: 'yes I’m interested',
      click_target: clickTarget,
      community_id: community.CommunityId,
      community_name: community.CommunityName,
      community_status: community.MarketingStatusLabel || defaultMarketingStatus,
      currency: 'USD',
      event_type: 'TDV Events',
      is_paid: 'False',
      is_priced: isPriced(community.PriceLow) || isPriced(community.PriceHigh) ? 'True' : 'False',
      is_refer_by_spotlight: listingInformation.wasMatchedAsSpotlightResult ? 'True' : 'False',
      // setting is_spotlight to false as spotlight is a concept that does not apply to recommended communities
      // clarification around this is needed, context in ticket: NHS20-6740
      is_spotlight: 'False',
      is_zero_results: isNoResults,
      location: getLocation(community.City, community.StateName),
      market_id: community.MarketId,
      market_name: community.MarketName,
      market_session: getMarketSession(community.MarketId, community.MarketName, community.StateAbbreviation),
      max_price: community.PriceHigh,
      min_price: community.PriceLow,
      org_button_name: modalInformation.buttonAction,
      position: getPosition(listingInformation.index),
      trustbuilder_reviews: community.HasBuilderReviews,
      value: 30,
    };

    store.dispatch(googleAnalytics4Event({ eventName: 'basic_direct_rec_lead', customProperties }));
  });
}

recommendedCommunitiesSubmittedBasic$.subscribe(triggerRecommendedBasicCommunitySubmittedEvents);

/**
 * Triggers the events for the recommended communities that were selected.
 * @param {Object} modalInformation Information related to the modal.
 */
function triggerRecommendedCommunitySubmittedEvents(modalInformation) {
  const { selectedRecommendedCommunities, listingInformation } = modalInformation;
  selectedRecommendedCommunities.forEach((community) => {
    const customProperties = {
      button_name: 'Get More Info',
      click_target: getClickTargetText(modalInformation.summaryCardCta != null),
      community_id: community.CommunityId,
      community_name: community.CommunityName,
      community_status: community.MarketingStatusLabel || defaultMarketingStatus,
      currency: 'USD',
      event_type: 'TDV Events',
      is_paid: 'True',
      is_priced: isPriced(community.PriceLow) || isPriced(community.PriceHigh) ? 'True' : 'False',
      is_refer_by_spotlight: listingInformation.wasMatchedAsSpotlightResult ? 'True' : 'False',
      // setting is_spotlight to false as spotlight is a concept that does not apply to recommended communities
      // clarification around this is needed, context in ticket: NHS20-6740
      is_spotlight: 'False',
      is_zero_results: isNoResultsPage(),
      location: getLocation(community.City, community.StateName),
      market_id: community.MarketId,
      market_name: community.MarketName,
      market_session: getMarketSession(community.MarketId, community.MarketName, community.StateAbbreviation),
      max_price: community.PriceHigh,
      min_price: community.PriceLow,
      org_button_name: modalInformation.buttonAction,
      position: getPosition(listingInformation.index),
      trustbuilder_reviews: community.HasBuilderReviews,
      value: 30,
    };

    store.dispatch(googleAnalytics4Event({ eventName: 'direct_rec_lead', customProperties }));
  });
}

recommendedCommunitiesSubmitted$.subscribe(triggerRecommendedCommunitySubmittedEvents);

/**
 * triggerPaidHomeModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidPlanModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidPlanCardProperties(listingInformation),
    realtor_question: getRealtorQuestionAnswerText(),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'generate_lead', customProperties }));
}

paidHomeModalSubmission$
  .pipe(
    filter((modalInformation) => modalInformation.type === 'plan'),
    filter((modalInformation) => modalInformation.variation !== 'basic'),
    filter((modalInformation) => modalInformation.leadGeneratorOpener !== LeadFormType.AskQuestionFormPaid.toString()),
  )
  .subscribe(triggerPaidPlanModalSubmitted);
paidAskAQuestionPlanModalSubmission$.subscribe(triggerPaidPlanModalSubmitted);

/**
 * triggerBasicHomeModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Learn More
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerBasicHomeModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const cardElement = document.querySelector(`[data-plan-id="${listingInformation.planId}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidPlanCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'basic_lead_submitted', customProperties }));
}

basicPlanModalSubmission$
  .pipe(
    filter((modalInformation) => modalInformation.type === 'plan'),
    filter((modalInformation) => modalInformation.variation === 'basic'),
  )
  .subscribe(triggerBasicHomeModalSubmitted);

/**
 * triggerBasicSpecModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Learn More
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerBasicSpecModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const cardElement = document.querySelector(`[data-spec-id="${listingInformation.specId}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidSpecCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'basic_lead_submitted', customProperties }));
}

basicSpecModalSubmission$
  .pipe(
    filter((modalInformation) => modalInformation.type === 'spec'),
    filter((modalInformation) => modalInformation.variation === 'basic'),
  )
  .subscribe(triggerBasicSpecModalSubmitted);

/**
 * triggerBasicListingModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Learn More
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerBasicListingModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const cardElement = document.querySelector(`[data-id="${listingInformation.id}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...getPaidSpecCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'basic_lead_submitted', customProperties }));
}

basicListingModalSubmission$
  .pipe(
    filter((modalInformation) => modalInformation.type === 'basicListing'),
    filter((modalInformation) => modalInformation.variation === 'basic'),
  )
  .subscribe(triggerBasicListingModalSubmitted);

/**
 * triggerPaidPlanModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidSpecModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResultsPage(),
    ...getPaidSpecCardProperties(listingInformation),
    realtor_question: getRealtorQuestionAnswerText(),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'generate_lead', customProperties }));
}

paidHomeModalSubmission$
  .pipe(
    filter((modalInformation) => modalInformation.type === 'spec'),
    filter((modalInformation) => modalInformation.variation !== 'basic'),
  )
  .subscribe(triggerPaidSpecModalSubmitted);
paidAskAQuestionSpecModalSubmission$.subscribe(triggerPaidSpecModalSubmitted);

// Recommended communities
/**
 * Triggers a Google Analytics 4 event when the Skip button is clicked in the More Recommended Communities modal.
 */
const skipModalRecommendedCommunitiesClick$ = fromEvent(document.body, 'click').pipe(
  filter((event) => event.target.closest('[data-skip-recommendations]')),
);

/**
 * Triggers a Google Analytics 4 event when the close button is clicked in the More Recommended Communities modal.
 */
const closeModalRecommendedCommunitiesClick$ = fromEvent(document.body, 'click').pipe(
  filter((event) => event.target.closest('[data-close-recommended]')),
);

/**
 * getCustomPropertiesByType: Determines and returns custom properties based on
 * the product type (type) and whether it is basic (basic) or not.
 * @param {object} type Housing Product Type.
 * @param {object} data Information related to modal and the card.
 * @returns Card information according to its type.
 */
function getCustomPropertiesByType(type, data) {
  const isBasic = data.isBasic === 'true';
  if (type === HousingProductType.Community().getName() && isBasic) {
    return getBasicCommunityCardProperties(data);
  }
  if (type === HousingProductType.Community().getName() && !isBasic) {
    return getPaidCommunityCardProperties(data);
  }
  if (type === HousingProductType.Spec().getName() && isBasic) {
    return getBasicSpecCardProperties(data);
  }
  if (type === HousingProductType.Spec().getName() && !isBasic) {
    return getPaidSpecCardProperties(data);
  }
  if (type === HousingProductType.Plan().getName() && isBasic) {
    return getBasicPlanCardProperties(data);
  }
  if (type === HousingProductType.Plan().getName() && !isBasic) {
    return getPaidPlanCardProperties(data);
  }
  if (type === HousingProductType.BasicListing().getName()) {
    return getBasicListingCardProperties(data);
  }
  return {};
}
/**
 * handleModalRecommendedCommunitiesFormSkip: Triggers the event when we press the Skip button in the
 * "More recommended communities" modal.
 * @param {object} modalInformation The information related to the modal opened.
 */
function handleModalRecommendedCommunitiesFormSkip(modalInformation) {
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });
  const customPropertiesByType = getCustomPropertiesByType(modalInformation.type, listingInformation);
  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...customPropertiesByType,
    realtor_question: getRealtorQuestionAnswerText(),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'direc_form_skip', customProperties }));
}

/**
 * When the Skip button is clicked on the More Recommended Communities modal,
 * we subscribe to the 'recommendedCommunitiesShow$' observer to get the information from the modal.
 */
skipModalRecommendedCommunitiesClick$
  .pipe(
    withLatestFrom(recommendedCommunitiesShow$),
    map(([, modalInfo]) => modalInfo),
  )
  .subscribe(handleModalRecommendedCommunitiesFormSkip);

/**
 * handleModalRecommendedCommunitiesShow: Triggers the event when we press the Skip button in the
 * "More recommended communities" modal.
 * @param {object} modalInformation The information related to the modal opened.
 */
function handleModalRecommendedCommunitiesShow(modalInformation) {
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customPropertiesByType = getCustomPropertiesByType(modalInformation.type, listingInformation);

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...customPropertiesByType,
    realtor_question: getRealtorQuestionAnswerText(),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'direc_section_view', customProperties }));
}

/**
 * When the More Recommended Communities modal is displayed,
 * we subscribe to the 'recommendedCommunitiesShow$' observer to get the information from the modal.
 */
recommendedCommunitiesShow$.subscribe(handleModalRecommendedCommunitiesShow);

/**
 * handleModalRecommendedCommunitiesClose: Triggers the event when we press the close button in the
 * "More recommended communities" modal.
 * @param {object} modalInformation The information related to the modal opened.
 */
function handleModalRecommendedCommunitiesClose(modalInformation) {
  const { listingInformation } = modalInformation;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: listingInformation?.clickTarget,
  });

  const customPropertiesByType = getCustomPropertiesByType(modalInformation.type, listingInformation);

  const customProperties = {
    click_target: clickTarget,
    event_type: 'TDV Events',
    currency: 'USD',
    value: 30,
    button_name: modalInformation.buttonAction,
    is_zero_results: isNoResults,
    ...customPropertiesByType,
    realtor_question: getRealtorQuestionAnswerText(),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'direc_form_close', customProperties }));
}

/**
 * When we click the close button on the recommended communities modal,
 * we subscribe to the `recommendedCommunitiesShow$` observer to get the modal information.
 */
closeModalRecommendedCommunitiesClick$
  .pipe(
    withLatestFrom(recommendedCommunitiesShow$),
    map(([, modalInfo]) => modalInfo),
  )
  .subscribe(handleModalRecommendedCommunitiesClose);

// ! Thank you modal more detail Button Click Event //
const triggerCommunityPaidDetailAnchorClickEvent = (id) => {
  const cardElement = document.querySelector(`[data-community-id="${id}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: 'Card clicked',
    is_zero_results: isNoResults,
    ...getPaidCommunityCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

detailAnchorClicked$
  .pipe(
    filter((information) => information.listingType === 'community'),
    map((information) => information.listingId),
  )
  .subscribe(triggerCommunityPaidDetailAnchorClickEvent);

const triggerPlanPaidDetailAnchorClickEvent = (id) => {
  const cardElement = document.querySelector(`[data-plan-id="${id}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: 'Card clicked',
    is_zero_results: isNoResults,
    ...getPaidPlanCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

detailAnchorClicked$
  .pipe(
    filter((information) => information.listingType === 'plan'),
    map((information) => information.listingId),
  )
  .subscribe(triggerPlanPaidDetailAnchorClickEvent);

const triggerSpecPaidDetailAnchorClickEvent = (id) => {
  const cardElement = document.querySelector(`[data-spec-id="${id}"]`);
  if (!cardElement) {
    return;
  }
  const cardDataset = cardElement.dataset;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: cardDataset.clickTarget,
  });

  const customProperties = {
    click_target: clickTarget,
    click_target1: 'Card clicked',
    is_zero_results: isNoResults,
    ...getPaidSpecCardProperties(cardDataset),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'card_clicked', customProperties }));
};

detailAnchorClicked$
  .pipe(
    filter((information) => information.listingType === 'spec'),
    map((information) => information.listingId),
  )
  .subscribe(triggerSpecPaidDetailAnchorClickEvent);

// #endregion Brochure Modal

// #region Layout

const footerLinks = document.querySelector('[data-footer-links]');
const footerLinksClicks$ = fromEvent(footerLinks, 'click');

/** According to the clicked event with this dictionary,
 * we can obtain the name of the click target and the name of the event.
 * Example: I click footer_55_plus, its click_target: 'FT - New 55+ Communities' and eventName: 'buyingguide_events'.
 * */
const clickedLinkFooter = {
  footer_55_plus: { name: 'FT - New 55+ Communities', eventName: 'buyingguide_events' },
  footer_condos_townhouses: { name: 'FT - Search New Condos', eventName: 'site_links' },
  footer_custom_homes_center: { name: 'FT - Custom home buyers', eventName: 'buyingguide_events' },
  footer_first_time_homebuyers: { name: 'FT - New First Time Buyers Communities', eventName: 'buyingguide_events' },
  footer_luxury_homes: { name: 'FT - Search Luxury Homes', eventName: 'site_links' },
  footer_modular_homes: { name: 'FT - Search Modular Homes', eventName: 'site_links' },
  footer_learning_center: { name: 'FT - Learning Center', eventName: 'site_links' },
  footer_ratings_and_reviews: { name: 'FT - TrustBuilder Ratings Review', eventName: 'site_links' },
  footer_real_estate_professionals: { name: 'FT - New Home Source Professional', eventName: 'outbound_links' },
  footer_livabl: { name: 'FT - Livabl', eventName: 'outbound_links' },
  footer_houseplans: { name: 'FT - Houseplans', eventName: 'outbound_links' },
  footer_casas_nuevas: { name: 'FT - Casas Nuevas', eventName: 'outbound_links' },
  footer_start_fresh_buy_new: { name: 'FT-StartFreshBuyNew', eventName: 'outbound_links' },
  footer_about_us: { name: 'FT - About us', eventName: 'site_links' },
  footer_contact_us: { name: 'FT - Contact us', eventName: 'site_links' },
  footer_list_your_homes: { name: 'FT - List Your Homes', eventName: 'outbound_links' },
  footer_unsubscribe: { name: 'FT - Unsubscribe', eventName: 'site_links' },
  footer_accessibility: { name: 'FT - Accessibility statement', eventName: 'site_links' },
  footer_terms_of_use: { name: 'FT - Terms of use', eventName: 'site_links' },
  footer_privacy_policy: { name: 'FT - Privacy policy', eventName: 'site_links' },
  footer_site_help: { name: 'FT - Site Help', eventName: 'site_links' },
  footer_do_not_sell_my_info: { name: 'FT - Do Not Sell My Info', eventName: 'site_links' },
  footer_facebook_link: { name: 'Facebook', eventName: 'outbound_links' },
  footer_pinterest_link: { name: 'Pinterest', eventName: 'outbound_links' },
  footer_twitter_link: { name: 'Twitter', eventName: 'outbound_links' },
  footer_youtube_link: { name: 'YouTube', eventName: 'outbound_links' },
  footer_instagram_link: { name: 'Instagram', eventName: 'outbound_links' },
};

const footerLinkClicked$ = footerLinksClicks$.pipe(
  filter((event) => event.target.closest('[data-footer-link]')),
  map((event) => event.target),
);

const triggerFooterLinksClickEvent = (element) => {
  const elementName = element.dataset.footerLink ?? element?.closest('[data-footer-link]')?.dataset?.footerLink;
  const nameLinkClicked = clickedLinkFooter[elementName]?.name;
  const eventNameLinkClicked = clickedLinkFooter[elementName]?.eventName;

  const customProperties = {
    click_target: nameLinkClicked,
  };

  store.dispatch(googleAnalytics4Event({ eventName: eventNameLinkClicked, customProperties }));
};

footerLinkClicked$.subscribe(triggerFooterLinksClickEvent);

const logoHeaderElement = document.querySelector('[data-header-logo]');
const logoHeaderClick$ = fromEvent(logoHeaderElement, 'click');

/**
 * Triggers a Google Analytics 4 event when the NewHomeSource header logo is clicked.
 */
function triggerLogoHeaderClickEvent() {
  const customProperties = {
    click_target: 'NHS Logo',
  };

  store.dispatch(googleAnalytics4Event({ eventName: siteLinksEventName, customProperties }));
}

logoHeaderClick$.subscribe(triggerLogoHeaderClickEvent);

const metroAreaLinksContainerElement = document.querySelector('[data-find-a-home] [data-metro-links]');
const metroAreaLinkClick$ = fromEvent(metroAreaLinksContainerElement, 'click');
const homesByStateLinksContainerElement = document.querySelector('[data-find-a-home] [data-state-links]');
const homesByStateLinkClick$ = fromEvent(homesByStateLinksContainerElement, 'click');

/**
 * Triggers a Google Analytics 4 event related to a clicked element in Find a Home panel.
 * @param clickedElement - Element that was clicked by the user.
 * @param sectionName - Represents the name of the section in which the link was clicked.
 * It is inside of the Find a Home panel
 */
function triggerFindAHomeLinkClickEvent(clickedElement, sectionName) {
  const customProperties = {
    click_target: `Global Header - ${sectionName}`,
    search_value: clickedElement.textContent,
    destination: clickedElement.href,
  };

  store.dispatch(googleAnalytics4Event({ eventName: siteLinksEventName, customProperties }));
}

metroAreaLinkClick$
  .pipe(
    map((event) => event.target),
    filter((element) => element.href),
  )
  .subscribe((element) => triggerFindAHomeLinkClickEvent(element, 'Metro Area'));

homesByStateLinkClick$
  .pipe(
    map((event) => event.target),
    filter((element) => element.href),
  )
  .subscribe((element) => triggerFindAHomeLinkClickEvent(element, 'New Home States'));

const resourcesContainerElement = document.querySelector('[data-resources]');
const resourceLinkClick$ = fromEvent(resourcesContainerElement, 'click');
const advertisingLinkElement = document.querySelector('[data-nav-links] [data-nav-link="header_advertise"]');
const advertisingLinkClick$ = fromEvent(advertisingLinkElement, 'click');

const navLinksEventNames = {
  header_advertise: outboundLinksEventName,
  header_reviews: siteLinksEventName,
  header_learn: siteLinksEventName,
  header_first_time_homebuyers: buyingGuideEventsName,
  header_custom_homes_center: buyingGuideEventsName,
  header_55_plus: buyingGuideEventsName,
  header_military_buyers: buyingGuideEventsName,
  header_affordability_calculator: siteLinksEventName,
  header_home_buying_tools: siteLinksEventName,
  header_credit_score: siteLinksEventName,
  header_mortgage_calculator: siteLinksEventName,
  header_get_preapproved: siteLinksEventName,
  header_prep_home_for_sale: siteLinksEventName,
  header_homeowners_insurance: siteLinksEventName,
  header_get_offers: siteLinksEventName,
  header_home_buying_tools_landing: siteLinksEventName,
};

const navLinksNames = {
  header_advertise: 'Builder Advertising',
  header_reviews: 'TB Rating',
  header_learn: 'HLC clicks',
  header_first_time_homebuyers: 'FTHB clicks',
  header_custom_homes_center: 'CHB clicks',
  header_55_plus: '55+ HB clicks',
  header_military_buyers: 'Military clicks',
  header_affordability_calculator: 'Af Calc clicks',
  header_home_buying_tools: 'HC Calc clicks',
  header_credit_score: 'Cdt Score clicks',
  header_mortgage_calculator: 'Mtg Calc clicks',
  header_get_preapproved: 'GP Approved clicks',
  header_prep_home_for_sale: 'PHF Sale clicks',
  header_homeowners_insurance: 'Hinsurance clicks',
  header_get_offers: 'Goffers clicks',
  header_home_buying_tools_landing: 'HB Tools clicks',
};

/**
 * Triggers a Google Analytics 4 event based on the provided navigation link value.
 * @param navLinkValue - Represents the specific navigation link that was clicked in the global header.
 */
function triggerHeaderLinkClickEvent(navLinkValue) {
  const customProperties = {
    click_target: `Global Header - ${navLinksNames[navLinkValue]}`,
  };

  store.dispatch(googleAnalytics4Event({ eventName: navLinksEventNames[navLinkValue], customProperties }));
}

resourceLinkClick$
  .pipe(
    map((event) => event.target),
    filter((element) => element.dataset.navLink && element.href),
    map((element) => element.dataset.navLink),
  )
  .subscribe(triggerHeaderLinkClickEvent);

advertisingLinkClick$.pipe(map((event) => event.target.dataset.navLink)).subscribe(triggerHeaderLinkClickEvent);

const signInClickTargetValue = 'Global Header - Sign In';

/**
 * Triggers a Google Analytics 4 event  when a sign-in form is closed.
 */
function triggerCloseSignInFormClickEvent() {
  const customProperties = {
    click_target: signInClickTargetValue,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

const signEventNames = {
  'Sign in': 'login',
  'Sign Up': 'sign-up',
};

/**
 * Triggers the event on sign-in buttons and dispatches a Google Analytics 4 event.
 * @param clickedElement - It is the element that was clicked by the user, typically a button or a link that
 * triggers the sign-in form submission.
 * This event is triggered by clicks and not when the sign transaction is done.
 */
function triggerSignInFormClickEvent(clickedElement) {
  const formContainer = clickedElement.closest('.auth0-lock-widget-container');
  const signOptionSelected = formContainer.querySelector('.auth0-lock-tabs .auth0-lock-tabs-current').textContent;

  const customProperties = {
    click_target: signInClickTargetValue,
    method: clickedElement.dataset.provider || 'Auth0',
    button_name: clickedElement.textContent,
  };

  store.dispatch(googleAnalytics4Event({ eventName: signEventNames[signOptionSelected], customProperties }));
}

pageWindowClick$
  .pipe(
    // We cannot add data attributes on the View as this form is external to NewHomeSource repository.
    map((event) => event.target.closest('.auth0-lock-close-button')),
    filter(Boolean),
  )
  .subscribe(triggerCloseSignInFormClickEvent);

pageWindowClick$
  .pipe(
    // We cannot add data attributes on the View as this form is external to NewHomeSource repository.
    map((event) => event.target.closest('.auth0-lock-social-button') || event.target.closest('.auth0-lock-submit')),
    filter(Boolean),
  )
  .subscribe(triggerSignInFormClickEvent);

// #endregion Layout

// #region Map

const mapContainerElement = document.querySelector('[data-map]');
const mapClick$ = fromEvent(mapContainerElement, 'click');

const mapToggleButtonElement = document.getElementById('btn-map-view');
const mapToggleButtonClick$ = fromEvent(mapToggleButtonElement, 'click');

// As the state element value changes before we can trigger the event, so we have
// to manage as it already changed. In other words, if we get a new click and the current state
// is map, it means before the button were on list as state, so the click target value is Mobile Map View.
const toggleClickTargetValues = {
  list: 'Mobile Map View',
  map: 'Mobile List View',
};

/**
 * Triggers a Google Analytics 4 event related to the map toggle button click.
 * @param elementState - Current state of the toggle button. It represents the new view on screen.
 */
const triggerMapToggleButtonClickEvent = (elementState) => {
  const customProperties = {
    click_target: toggleClickTargetValues[elementState],
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_events', customProperties }));
};

mapToggleButtonClick$
  .pipe(
    map((event) => event.target),
    map((element) => element.closest('#btn-map-view')),
    map((element) => element.dataset.state),
    filter(Boolean),
  )
  .subscribe(triggerMapToggleButtonClickEvent);

/**
 * Triggers the Google Analytics 4 event related to a map zoom button click.
 * @param zoomButton - Element that is used to trigger a zoom action by a click on the map.
 */
const triggerZoomClickEvent = (zoomButton) => {
  const customProperties = {
    click_target: `${zoomButton.title} map`,
    click_target1: 'On Click',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_events', customProperties }));
};

mapClick$
  .pipe(
    map((event) => event.target),
    filter((element) => element.closest('[data-control-width][data-control-height]')),
    filter((element) => element.title),
  )
  .subscribe(triggerZoomClickEvent);

const mapContainerTouchstart$ = fromEvent(mapContainerElement, 'touchstart');
const mapContainerTouchend$ = fromEvent(mapContainerElement, 'touchend');
const mapZoomChange$ = fromEvent(mapContainerElement, 'zoomChanged');

const mapPinchStart$ = mapContainerTouchstart$.pipe(filter((touchstartEvent) => touchstartEvent.touches.length > 1));
const mapPinchEnd$ = mapContainerTouchend$.pipe(filter((touchendEvent) => touchendEvent.touches.length > 1));

const mapPinch$ = merge(mapPinchStart$, mapPinchEnd$);

const mapZoomPinch$ = mapZoomChange$.pipe(
  withLatestFrom(mapPinch$),

  // We have to validate if the last pinch was starting, otherwise it will mean
  // the zoom was caused by other reason.
  filter(([, lastPinchEvent]) => lastPinchEvent.type === 'touchstart'),
  map(([mapZoomChangeEvent]) => mapZoomChangeEvent.detail.isZoomIn),
);

/**
 * Triggers a Google Analytics 4 event based on whether the user is zooming in or out on a map using pinch gesture.
 * @param isZoomIn - Boolean value that indicates whether the map was zoom in or zoom out.
 */
const triggerMapZoomByPinch = (isZoomIn) => {
  const customProperties = {
    click_target: isZoomIn ? 'Zoom in map' : 'Zoom out map',
    click_target1: 'On Pinch',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_events', customProperties }));
};

mapZoomPinch$.subscribe(triggerMapZoomByPinch);

const mapWheel$ = fromEvent(mapContainerElement, 'wheel');

const mapZoomScroll$ = mapZoomChange$.pipe(
  // There's scenarios where the wheel event is triggered after the zoomChanged event.
  // So we have to wait a bit to get the latest wheel event.
  delay(50),
  withLatestFrom(mapWheel$),

  // The time when it is triggered could change. Sometimes the wheel event is triggered first, and sometimes
  // the zoomChanged event is triggered first. So we add a threshold time to validate if the wheel event
  // is related to the zoomChanged event.
  filter(([lastMapZoomEvent, lastMapWheelEvent]) => {
    const thresholdTime = 500;
    const maxTime = lastMapZoomEvent.timeStamp + thresholdTime;
    const minTime = lastMapZoomEvent.timeStamp - thresholdTime;

    return minTime < lastMapWheelEvent.timeStamp && lastMapWheelEvent.timeStamp < maxTime;
  }),
  map(([mapZoomChangeEvent]) => mapZoomChangeEvent.detail.isZoomIn),
);

/**
 * Triggers a Google Analytics 4 event based on whether the user is zooming in or out on a map using scrolling.
 * @param isZoomIn - Boolean value that indicates whether the map was zoom in or zoom out.
 */
const triggerMapZoomByScroll = (isZoomIn) => {
  const customProperties = {
    click_target: isZoomIn ? 'Zoom in map' : 'Zoom out map',
    click_target1: 'On Scroll',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_events', customProperties }));
};

mapZoomScroll$.subscribe(triggerMapZoomByScroll);

// #endregion Map

// #region Filter Menu
const facetPanel = document.querySelector('[data-facets-panel]');
const facetPanelClicks$ = fromEvent(facetPanel, 'click');

/**
 * We assign the properties according to the clicked element.
 */
const getPropertiesFacets = {
  'price-toggle': { filter_name: 'Price', click_target: 'Filter Clicked' },
  'bedbath-toggle': { filter_name: 'Bed Bath', click_target: 'Filter Clicked' },
  'movein-date': { filter_name: 'Move In', click_target: 'Filter Clicked' },
  'allfilters-toggle': { filter_name: 'Advanced Search', click_target: 'Filter Clicked' },
  'allfilters-clearAll': { filter_name: 'Advanced Search', click_target: 'Clear Filters' },
  'allfilters-close': { filter_name: 'Advanced Search', click_target: 'Filter Closed' },
};

const facetClicked$ = facetPanelClicks$.pipe(
  map((event) => event.target.closest('[data-panel-option]')),
  filter(Boolean),
);

/**
 * Triggers a Google Analytics 4 event when one of the filter panels,
 * clearAll, or close of the advanced panel is clicked.
 * @param element - Represents the DOM element clicked.
 */
const triggerFacetsClickEvent = (element) => {
  const option = element.dataset.panelOption;
  const customProperties = {
    ...getPropertiesFacets[option],
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};

facetClicked$.subscribe(triggerFacetsClickEvent);

// Typeahead
/**
 * getSearchLocation: Gets the search location information based on what's on the search box
 * @param search All the location information: market, state, zip...
 * @returns The value with search location information.
 */
const getSearchLocation = (search) => {
  const { locationType, marketName, city, marketState, zip, county, neighborhood, schoolDistrictName } = search;

  /* Search depending on the different search types */
  const searchLocationDictionary = {
    market: `${marketName} ${marketState}, Area`,
    city: `${city}, ${marketState}`,
    zip,
    county: `${county} County, ${marketState}`,
    neighborhood: `${neighborhood} Neighborhood, ${marketName} ${marketState}`,
    schoolDistrict: `${schoolDistrictName}`,
    default: undefined,
  };

  const searchType = searchLocationDictionary[locationType];

  return searchType;
};

/**
 * Trigger a Google Analytics 4 event when the search is changed.
 * @param searchInformation - Represents the properties of the location.
 */
const triggerSearchEvent = (searchInformation) => {
  const search = getSearchLocation(searchInformation);
  if (!search) {
    return;
  }
  const customProperties = {
    filter_name: 'location_typeahead_search',
    search_value: search,
    current_location: search,
    click_target: 'SearchBarResult',
    click_target2: searchInformation.locationType,
  };
  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties })), 0);
};

store$.pipe(map(locationSearchParameters), distinctUntilChanged(), skip(1)).subscribe(triggerSearchEvent);

const searchBarCompletionSuggestionTypes = {
  StateEquivalent: 'state',
  City: 'city',
  CountyEquivalent: 'county',
  Neighborhood: 'neighborhood',
  PostalCode: 'zip',
  SchoolDistrict: 'schoolDistrict',
  MarketAreaPopularlyNamedRegion: 'market',
  Article: 'article',
  Community: 'community',
  Builder: 'builder',
  MasterPlannedCommunity: 'mpc',
};

const triggerNonSrpRedirectSearchEvent = (communityResult) => {
  const customProperties = {
    filter_name: 'location_typeahead_search',
    search_value: communityResult.name,
    current_location: communityResult.locationUrl,
    click_target: 'SearchBarResult',
    click_target2: searchBarCompletionSuggestionTypes[communityResult.completionSuggestionType] || 'unknown',
  };
  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties })), 0);
};

const selectSearchResultEvent$ = fromEvent(document.body, 'search_result_non_srp_redirect').pipe(
  map((event) => event.detail),
  filter(
    (result) =>
      result &&
      (result.categoryType === SearchBarResultCategory.Community.id ||
        result.categoryType === SearchBarResultCategory.Article.id ||
        result.categoryType === SearchBarResultCategory.Builder.id ||
        (result.categoryType === SearchBarResultCategory.Location.id &&
          result.completionSuggestionType === CompletionSuggestionType.State.name)),
  ),
);

selectSearchResultEvent$.subscribe(triggerNonSrpRedirectSearchEvent);

// Price
const priceContainer = document.querySelector('[data-price-slider-container] [data-slider]');

const getPriceSliderChangeEvent = (handler) => priceContainer.noUiSlider.on('change', handler);
const priceChanged$ = fromEventPattern(getPriceSliderChangeEvent.bind(this)).pipe(map(([newValues]) => newValues));

/**
 * Triggers a Google Analytics 4 event when the price filter is changed.
 * @param newValues - Represents the new values in the price.
 */
const triggerPriceChangeEvent = (newValues) => {
  const minPriceIndex = 0;
  const maxPriceIndex = 1;
  const customProperties = {
    filter_name: 'Price',
    click_target: 'Filter Applied',
    min_price: Math.trunc(newValues[minPriceIndex]),
    max_price: Math.trunc(newValues[maxPriceIndex]),
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};
priceChanged$.subscribe(triggerPriceChangeEvent);

// bedrooms and bathrooms
const bedFilter = document.querySelector('[data-bedrooms-group]');
const bathFilter = document.querySelector('[data-bathrooms-group]');
const panelBathChange$ = fromEvent(bathFilter, 'change').pipe(map((event) => event.target));
const panelBedChange$ = fromEvent(bedFilter, 'change').pipe(map((event) => event.target));

/**
 * Triggers a Google Analytics 4 event when the bedrooms or bathrooms filter is clicked.
 * @param homeFacts Represents the list of home properties,
 * in this case extract bedrooms and bathrooms from it.
 */
const triggerbedBathFilterClickEvent = (homeFacts) => {
  const bedValue = homeFacts.bedrooms;
  const bathValue = homeFacts.bathrooms;
  const customProperties = {
    filter_name: 'Bed Bath',
    click_target: 'Filter Applied',
    bedrooms: bedValue,
    bathrooms: bathValue,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};

const homeFacts$ = store$.pipe(map(homeFactsSelectors), distinctUntilChanged());

/**
 * combine the observable of panelBathChange$ and panelChange$ so that when either is emitted, the event is triggered.
 * use homeFacts$ to get the most recent values of bedrooms and bathrooms
 */
merge(panelBathChange$, panelBedChange$)
  .pipe(
    withLatestFrom(homeFacts$),
    map(([, homeFact]) => homeFact),
  )
  .subscribe(triggerbedBathFilterClickEvent);

// movein
const moveInDateFilter = document.querySelector('[data-move-in-date-filter]');
const moveInDatePanelChange$ = fromEvent(moveInDateFilter, 'change').pipe(
  filter((event) => event.target.closest('[data-move-in-date-filter]')),
  map((event) => event.target),
);

/**
 * Triggers a Google Analytics 4 event when any of the options in the moveInDate panel is clicked.
 * @param {*} element Represents the DOM element clicked on the panel.
 */
const triggermoveinDateFilterClickEvent = (element) => {
  const customProperties = {
    filter_name: 'Move In',
    click_target: 'Filter Applied',
    move_in_date: element.value,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};

moveInDatePanelChange$.subscribe(triggermoveinDateFilterClickEvent);

// Sort By
const sortChanged$ = store$.pipe(map(selectSorting), distinctUntilChanged(), skip(1));
const sortSelectElement = document.querySelector('[data-sort-select]');

/**
 * getSortingLabel: Gets the label to be used on the sort value on GA4 events.
 * @param {object} sortingInformation Information related to sorting.
 * @returns The label to be used related to the sort value.
 */
function getSortingLabel(sortingInformation) {
  const sortingValue = `${sortingInformation?.sortBy},${sortingInformation?.sortOrder}`;
  const sortingOption = sortSelectElement.querySelector(`[value="${sortingValue}"]`);
  return sortingOption?.textContent;
}

/**
 * Triggers a Google Analytics 4 event when any of the options in the sort by is clicked.
 * @param {*} element Represents the DOM element clicked on the sort by.
 */
const triggerSortByOptionClickEvent = (sorting) => {
  const customProperties = {
    search_value: getSortingLabel(sorting),
    click_target: 'Sort by',
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};

sortChanged$.subscribe(triggerSortByOptionClickEvent);

// All filters
const allFiltersPanel = document.querySelector('[data-panel-allFilters]');
const allFiltersPanelChanges$ = fromEvent(allFiltersPanel, 'change').pipe(
  filter((event) => event.target.closest('[data-panel-allFilters]')),
  filter((event) => !event.target.closest('input#community-status-dropdown')),
  map((event) => event.target),
);

/**
 * Review the list of elements to get only those with true values.
 * @param elements
 * @returns A list with only the values that have true or undefined if no value was found.
 */
const getParametersActive = (elements) => {
  const activeFilters = Object.keys(elements).filter((key) => elements[key]);

  return activeFilters.length > 0 ? activeFilters : undefined;
};

const getOthersproperties = (element) => {
  const otherProperties = {
    reviews: element.reviews || undefined,
    hotdeals: element.hotDeals || undefined,
    price_reduction: element.priceReduction || undefined,
    listings_hoa_info: element.hasHomeOwnersAssociationFee || undefined,
    '55plus-any': element.any || undefined,
    '55plus-only': element.only || undefined,
    '55plus-exclude': element.exclude || undefined,
  };

  return getParametersActive(otherProperties);
};

/**
 * Get the value for the properties,
 * and add undefined to those that do not have a significant value so that
 * when the event fires it skips the undefined values.
 * @param element - property list.
 * @returns A new list with the values assigned to each property for the event.
 */
const getFiltersAppliedProperties = (element) => ({
  home_type: getParametersActive(element.housingOptions),
  community_status: getParametersActive(element.communityStatus),
  min_price: element.price.minimumPrice || undefined,
  max_price: element.price.maximumPrice || undefined,
  move_in_date: element.moveInDate || undefined,
  school_districts: element.brandAndSchools.schools || undefined,
  brand_id: element.brandAndSchools.brandId || undefined,
  bedrooms: element.homeFacts.bedrooms || undefined,
  bathrooms: element.homeFacts.bathrooms || undefined,
  living_areas: element.homeFacts.livingAreas || undefined,
  primary_bedroom: element.homeFacts.primaryBedroom || undefined,
  stories: element.homeFacts.stories || undefined,
  garages: element.homeFacts.garages || undefined,
  other_features: element.homeFacts.rvGarage || undefined,
  other_filters: getOthersproperties(element.otherFilters),
  amenities: getParametersActive(element.amenities),
});

/**
 * Triggers a Google Analytics 4 event when any of the filter options in the All filters panel is clicked.
 * @param elements Contains all updated filter values.
 */
const triggerAllFilterClickEvent = (elements) => {
  const customProperties = {
    filter_name: 'Advanced Search',
    click_target: 'Filter Applied',
    ...getFiltersAppliedProperties(elements),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};
const facetsSelector$ = store$.pipe(map(facetsPanelSelector), distinctUntilChanged());

/**
 * combine multiple observables into one using merge,
 * The 3 observables subscribe to triggerAllFilterClickEvent and use the same data source which is facetsSelector$,
 * all the observables are from different places in the allfilters panel,
 * priceChangedAdvance$ is from the price slider,
 * housingPanelClicks$ is from the housings option (luxury, custom, condos... .) and
 * allFiltersPanelChanges$ is everything else we have there and they react to the change event.
 * When you click on any of them, the entire list that comes in facetsSelector$ is read,
 * since the event emits all the active filters regardless of whether it is price,
 * bath/bed, schools, any active filter, so merge allows it.
 * for the priceChangedAdvance add a delay time using debounce so that
 * the price values are updated correctly in facetsPanelSelector.
 */
merge(allFiltersPanelChanges$)
  .pipe(
    withLatestFrom(facetsSelector$),
    map(([, facetsData]) => facetsData),
  )
  .subscribe(triggerAllFilterClickEvent);
// #endregion Filter Menu

// #region Popular searches pill
// Popular searches pill
const popularPillsFilters = document.querySelector('[data-popular-searches-pills-list]');
const popularPillsFiltersClicks$ = fromEvent(popularPillsFilters, 'click').pipe(map((event) => event.target));

/**
 * Get the value for the properties,
 * and add undefined to those that do not have a significant value so that
 * when the event fires it skips the undefined values.
 * When the golf course pill is removed from the popular searches pills, please delete the amenities property from here.
 * Ticket where that pill will be removed: https://builderhomesite.atlassian.net/browse/NHS20-2391.
 * @param element - property list.
 * @returns A new list with the values assigned to each property for the event.
 */
const getPopularPillsAppliedProperties = (element) => {
  const otherFilters = getOthersproperties(element.otherFilters);
  const comingSoon = element.comingsoon ? ['comingsoon'] : [];
  return {
    amenities: getParametersActive(element.amenities),
    move_in_date: element.moveInDate || undefined,
    other_filters: [...otherFilters, ...comingSoon],
    home_type: getParametersActive(element.housingOptions),
  };
};

/**
 * Triggers a Google Analytics 4 event when any of the filter options in the popular search pills is clicked.
 * @param elements Contains all updated filter values.
 */
const triggerPopularPillsFilterClickEvent = (elements) => {
  const customProperties = {
    filter_name: 'Popular pills',
    click_target: 'Filter Applied',
    ...getPopularPillsAppliedProperties(elements),
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'search_filters', customProperties }));
};

const popularPillsSelector$ = store$.pipe(map(selectLinkedPopularPillsFilters), distinctUntilChanged());

popularPillsFiltersClicks$
  .pipe(
    debounceTime(500),
    withLatestFrom(popularPillsSelector$),
    map(([, filtersPopular]) => filtersPopular),
  )
  .subscribe(triggerPopularPillsFilterClickEvent);
// #endregion Popular searches pill

/**
 * Triggers the carousel scrolled event for community markers.
 *
 * @param {Object} data - The data object containing carousel scroll information.
 */
function triggerCommunityMapMarkerClickedEvent(data) {
  const customProperties = {
    click_target: 'SRP Map Pins',
    community_id: data.id,
    community_name: data.metaData.communityName,
    currency: 'USD',
    event_type: 'TDV Events',
    is_paid: data.metaData.isPaid,
    is_priced: isPriced(data.metaData.priceLow) || isPriced(data.metaData.priceHigh),
    is_spotlight: data.wasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    max_price: data.metaData.priceHigh,
    min_price: data.metaData.priceLow,
    price_reduction: data.metaData.priceReducedTrackingLabel,
    type: MarkerType.HousingDevelopment.toString(),
    value: 1,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pin_clicked', customProperties }));
}

// stream used to subscribe to community marker click events
markerPinClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter(
      (marker) =>
        marker.type === MarkerType.HousingDevelopment.toString() || marker.type === MarkerType.BuildablePlan.toString(),
    ),
    distinctUntilChanged(),
  )
  .subscribe(triggerCommunityMapMarkerClickedEvent);

/**
 * Triggers the map marker clicked event for plan markers.
 *
 * @param {Object} marker - The marker object.
 */
function triggerPlanMapMarkerClickedEvent(marker) {
  const customProperties = {
    click_target: 'SRP Map Pins',
    currency: 'USD',
    event_type: 'TDV Events',
    is_paid: marker.metaData.isPaid,
    is_plan: true,
    is_priced: isPriced(marker.metaData.priceLow),
    is_spec: false,
    is_spotlight: marker.wasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    min_price: marker.metaData.priceLow,
    plan_id: marker.id,
    price_reduction: marker.metaData.priceReducedTrackingLabel,
    type: marker.type,
    value: 1,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pin_clicked', customProperties }));
}

// stream used to subscribe to plan marker click events
markerPinClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.PlanUnit.toString()),
    distinctUntilChanged(),
  )
  .subscribe(triggerPlanMapMarkerClickedEvent);

/**
 * triggerCommunityMapClickEvent will dispatch the event of clicking in a community.
 * static map.
 */
const triggerCommunityMapClickEvent = ({ communityMap }) => {
  const customProperties = {
    community_id: communityMap.id,
    community_name: communityMap.name,
    builder_id: communityMap.builderId,
    builder_name: communityMap.brandName,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'StaticMapClick', customProperties })), 0);
};

communityMapClicked$.subscribe(triggerCommunityMapClickEvent);

/**
 * triggerCommunityMapLotClickEvent will dispatch the event of clicking in a community lot on the map.
 */
const triggerCommunityMapLotClickEvent = ({ communityMap, lotId }) => {
  const customProperties = {
    community_id: communityMap.id,
    community_name: communityMap.name,
    builder_id: communityMap.builderId,
    builder_name: communityMap.brandName,
    lot_number: lotId,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'InteractiveMapLotClick', customProperties })), 0);
};

communityMapLotClicked$.subscribe(triggerCommunityMapLotClickEvent);

/**
 * triggerInteractiveSitePlanCtaSubmitEvent: Dispatches a Google Analytics 4 event when the
 * Interactive Site Plan brochure CTA is submitted.
 * This function constructs custom properties from the provided listing information and
 * triggers the 'InteractiveMapBrochureCTA' event.
 *
 * @param {Object} param0 - Object containing listing information.
 * @param {Object} param0.listingInformation - The listing information for the event.
 */
const triggerInteractiveSitePlanCtaSubmitEvent = ({ listingInformation }) => {
  const customProperties = {
    community_id: listingInformation.communityId,
    community_name: listingInformation.communityName,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
  };

  if (listingInformation.type === 'plan') {
    customProperties.plan_id = listingInformation.id;
    customProperties.plan_name = listingInformation.name;
  }

  if (listingInformation.type === 'spec') {
    customProperties.spec_id = listingInformation.id;
    customProperties.spec_address = listingInformation.address;
  }

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: `InteractiveMapBrochureCTA`, customProperties })), 0);
};

interactiveSitePlanBrochureSubmitted$.subscribe(triggerInteractiveSitePlanCtaSubmitEvent);

/**
 * triggerEnterInteractiveSiteStateEvent: Dispatches a Google Analytics 4 event when a user enters
 * the interactive site plan state.
 * This function constructs custom properties from the provided listing information and triggers
 * the 'InteractiveMapClick' event.
 *
 * @param {Object} param0 - Object containing listing information.
 * @param {Object} param0.communityMap - The listing information for the event.
 */
const triggerEnterInteractiveSiteStateEvent = ({ communityMap }) => {
  const customProperties = {
    community_id: communityMap.id,
    community_name: communityMap  .name,
    builder_id: communityMap.builderId,
    builder_name: communityMap.brandName,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: `InteractiveMapClick`, customProperties })), 0);
};

onEnterInteractiveSitePlanState$.subscribe(triggerEnterInteractiveSiteStateEvent);

/**
 * triggerCommunityMapSummaryCardClickEvent will dispatch the event of clicking in a community summary card on the map.
 */
const triggerCommunityMapSummaryCardClickEvent = ({ summaryCard }) => {
  const customProperties = {
    community_id: summaryCard.communityId,
    community_name: summaryCard.communityName,
    builder_id: summaryCard.builderId,
    builder_name: summaryCard.brandName,
    lot_number: summaryCard.lotId,
    spec_id: summaryCard.specId,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: `InteractiveMapLotCTAClick`, customProperties })), 0);
};

communityMapSummaryCardClicked$.subscribe(triggerCommunityMapSummaryCardClickEvent);

/**
 * Triggers the map marker clicked event for spec markers.
 *
 * @param {Object} marker - The marker object.
 */
function triggerSpecMapMarkerClickedEvent(marker) {
  const customProperties = {
    click_target: 'SRP Map Pins',
    currency: 'USD',
    event_type: 'TDV Events',
    is_paid: marker.metaData.isPaid,
    is_plan: false,
    is_priced: isPriced(marker.metaData.priceLow),
    is_spec: true,
    is_spotlight: marker.wasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    min_price: marker.metaData.priceLow,
    price_reduction: marker.metaData.priceReducedTrackingLabel,
    spec_id: marker.id,
    type: marker.type,
    value: 1,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'pin_clicked', customProperties }));
}

// stream used to subscribe to spec marker click events
markerPinClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.SpecUnit.toString()),
    distinctUntilChanged(),
  )
  .subscribe(triggerSpecMapMarkerClickedEvent);

/**
 * Triggers the carousel scrolled event for community markers.
 *
 * @param {Object} data - The data object containing carousel scroll information.
 */
function triggerCommunitySummaryCardLinkClickedEvent(data) {
  const customProperties = {
    builder_id: data.BuilderId,
    builder_name: data.BrandName,
    click_target: 'SRP Map Card',
    click_target1: 'Community Link Clicked',
    community_id: data.Id,
    community_name: data.Name,
    community_status: data.MarketingStatus?.Name,
    destination: data.Url,
    is_paid: !data.IsBasic,
    is_priced: Boolean(data.PriceLow) && data.PriceLow !== '0',
    is_spotlight: data.WasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    market_id: data.MarketId,
    max_price: data.PriceHigh,
    min_price: data.PriceLow,
    price_reduction: data.PriceReductionLabel,
    trustbuilder_reviews: data.BuilderRating > 0,
    type: data.Type,
    zip_code: data.Zip,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
}

/**
 * Triggers the map marker clicked event for plan markers.
 *
 * @param {Object} marker - The marker object.
 */
function triggerPlanSummaryCardLinkClickedEvent(marker) {
  const customProperties = {
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    click_target: 'SRP Map Card',
    click_target1: 'Home Link Clicked',
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    destination: marker.Url,
    home_status: marker.StatusLabel,
    is_paid: !marker.IsBasic,
    is_plan: true,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_spec: false,
    is_spotlight: marker.WasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    market_id: marker.MarketId,
    min_price: marker.PriceLow,
    plan_id: marker.Id,
    price_reduction: marker.PriceReductionLabel,
    trustbuilder_reviews: marker.BuilderRating > 0,
    type: marker.Type,
    zip_code: marker.Zip,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
}

/**
 * Triggers the map marker clicked event for spec markers.
 *
 * @param {Object} marker - The marker object.
 */
function triggerSpecSummaryCardLinkClickedEvent(marker) {
  const customProperties = {
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    click_target: 'SRP Map Card',
    click_target1: 'Home Link Clicked',
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    destination: marker.Url,
    home_status: marker.StatusLabel,
    is_paid: !marker.IsBasic,
    is_plan: false,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_spec: true,
    is_spotlight: marker.WasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    market_id: marker.MarketId,
    min_price: marker.PriceLow,
    price_reduction: marker.PriceReductionLabel,
    spec_id: marker.Id,
    trustbuilder_reviews: marker.BuilderRating > 0,
    type: marker.Type,
    zip_code: marker.Zip,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'map_card_clicked', customProperties }));
}

/**
 * Stream used to check when a new community card link is clicked.
 */
summaryCardLinkClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter(marker =>
      marker.Type === MarkerType.HousingDevelopment.toString() ||
      marker.Type === MarkerType.BuildablePlan.toString())
  )
  .subscribe(triggerCommunitySummaryCardLinkClickedEvent);

/**
 * Stream used to check when a new home card link is clicked.
 */
summaryCardLinkClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.Type === MarkerType.PlanUnit.toString()),
  )
  .subscribe(triggerPlanSummaryCardLinkClickedEvent);

/**
 * Stream used to check when a new home card link is clicked.
 */
summaryCardLinkClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.Type === MarkerType.SpecUnit.toString()),
  )
  .subscribe(triggerSpecSummaryCardLinkClickedEvent);

/**
 * Triggers the map marker clicked event.
 *
 * @param {Object} marker - The marker object.
 */
function triggerCommunitySummaryCardCallToActionClickEvent(detail) {
  const { marker, callToAction, target } = detail;
  const isNoResults = isNoResultsPage();
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: target,
  });

  const customProperties = {
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    button_name: callToAction,
    click_target: clickTarget,
    community_id: marker.Id,
    community_name: marker.Name,
    community_status: marker.MarketingStatus?.Name,
    is_paid: !marker.IsBasic,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_spotlight: marker.WasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResults,
    market_id: marker.MarketId,
    max_price: marker.PriceHigh,
    min_price: marker.PriceLow,
    price_reduction: marker.PriceReductionLabel,
    trustbuilder_reviews: marker.BuilderRating > 0,
    type: marker.Type,
    zip_code: marker.Zip,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
}

/**
 * Triggers the map marker clicked event.
 *
 * @param {Object} marker - The marker object.
 */
function triggerPlanSummaryCardCallToActionClickEvent(detail) {
  const { marker, callToAction, target } = detail;
  const isNoResults = isNoResultsPage();
  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: target,
  });

  const customProperties = {
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    button_name: callToAction,
    click_target: clickTarget,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    home_status: marker.StatusLabel,
    is_paid: !marker.IsBasic,
    is_plan: true,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_spec: false,
    is_spotlight: marker.WasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResults,
    market_id: marker.MarketId,
    min_price: marker.PriceLow,
    plan_id: marker.Id,
    price_reduction: marker.PriceReductionLabel,
    trustbuilder_reviews: marker.BuilderRating > 0,
    type: marker.Type,
    zip_code: marker.Zip,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
}

/**
 * Triggers the map marker clicked event.
 *
 * @param {Object} marker - The marker object.
 */
function triggerSpecSummaryCardCallToActionClickEvent(detail) {
  const { marker, callToAction, target } = detail;
  const isNoResults = isNoResultsPage();

  const clickTarget = getClickTarget({
    isNoResultsPage: isNoResults,
    clickTarget: target,
  });

  const customProperties = {
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    button_name: callToAction,
    click_target: clickTarget,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    home_status: marker.StatusLabel,
    is_paid: !marker.IsBasic,
    is_plan: false,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_spec: true,
    is_spotlight: marker.WasMatchedAsSpotlightResult ? 'True' : 'False',
    is_zero_results: isNoResultsPage(),
    market_id: marker.MarketId,
    min_price: marker.PriceLow,
    price_reduction: marker.PriceReductionLabel,
    spec_id: marker.Id,
    trustbuilder_reviews: marker.BuilderRating > 0,
    type: marker.Type,
    zip_code: marker.Zip,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties })));
}

/**
 * Triggers the community map marker url opened event.
 *
 * @param {Object} event - The event object containing the marker and interaction type details.
 * @param {Object} event.detail.marker - The marker object.
 * @param {string} event.detail.interactionType - The type of interaction that triggered the event.
 */
function triggerCommunityMarkerUrlOpenedEvent(data) {
  const { marker, interactionType } = data;

  const customProperties = {
    click_target: 'SRP Map Pins',
    click_target1: interactionType,
    community_name: marker.metaData.communityName,
    type: MarkerType.HousingDevelopment.toString(),
    min_price: marker.metaData.priceLow,
    max_price: marker.metaData.priceHigh,
    is_priced: isPriced(marker.metaData.priceLow) || isPriced(marker.metaData.priceHigh),
    is_paid: marker.metaData.isPaid,
    is_zero_results: isNoResultsPage(),
    community_id: marker.id,
    price_reduction: marker.metaData.priceReducedTrackingLabel,
    destination: marker.url,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'mappin_double_clicked', customProperties }));
}

markerUrlOpened$
  .pipe(
    filter((data) => Boolean(data?.marker?.id)),
    filter(
      (data) =>
        data?.marker?.type === MarkerType.HousingDevelopment.toString() ||
        data?.marker?.type === MarkerType.BuildablePlan.toString(),
    ),
  )
  .subscribe(triggerCommunityMarkerUrlOpenedEvent);

/**
 * Triggers the plan map marker url opened event.
 *
 * @param {Object} event - The event object containing the marker and interaction type details.
 * @param {Object} event.detail.marker - The marker object.
 * @param {string} event.detail.interactionType - The type of interaction that triggered the event.
 */
function triggerPlanMarkerUrlOpenedEvent(data) {
  const { marker, interactionType } = data;

  const customProperties = {
    click_target: 'SRP Map Pins',
    click_target1: interactionType,
    plan_id: marker.id,
    type: marker.type,
    min_price: marker.metaData.priceLow,
    is_priced: isPriced(marker.metaData.priceLow),
    is_paid: marker.metaData.isPaid,
    is_zero_results: isNoResultsPage(),
    price_reduction: marker.metaData.priceReducedTrackingLabel,
    is_plan: true,
    is_spec: false,
    destination: marker.url,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'mappin_double_clicked', customProperties }));
}

markerUrlOpened$
  .pipe(
    filter((data) => Boolean(data?.marker?.id)),
    filter((data) => data?.marker?.type === MarkerType.PlanUnit.toString()),
  )
  .subscribe(triggerPlanMarkerUrlOpenedEvent);

/**
 * Triggers the spec map marker url opened event.
 *
 * @param {Object} event - The event object containing the marker and interaction type details.
 * @param {Object} event.detail.marker - The marker object.
 * @param {string} event.detail.interactionType - The type of interaction that triggered the event.
 */
function triggerSpecMarkerUrlOpenedEvent(data) {
  const { marker, interactionType } = data;

  const customProperties = {
    click_target: 'SRP Map Pins',
    click_target1: interactionType,
    spec_id: marker.id,
    type: marker.type,
    min_price: marker.metaData.priceLow,
    is_priced: isPriced(marker.metaData.priceLow),
    is_paid: marker.metaData.isPaid,
    is_zero_results: isNoResultsPage(),
    price_reduction: marker.metaData.priceReducedTrackingLabel,
    is_plan: false,
    is_spec: true,
    destination: marker.url,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'mappin_double_clicked', customProperties }));
}

markerUrlOpened$
  .pipe(
    filter((data) => Boolean(data?.marker?.id)),
    filter((data) => data?.marker?.type === MarkerType.SpecUnit.toString()),
  )
  .subscribe(triggerSpecMarkerUrlOpenedEvent);

/**
 * Stream used to check when a community summary card call to action is clicked.
 */
summaryCardCtaClicked$
  .pipe(
    filter((detail) => Boolean(detail.marker)),
    filter((detail) => detail.marker.Type === MarkerType.HousingDevelopment.toString()),
    distinctUntilChanged(),
  )
  .subscribe(triggerCommunitySummaryCardCallToActionClickEvent);

/**
 * Stream used to check when a plan summary card call to action is clicked.
 */
summaryCardCtaClicked$
  .pipe(
    filter((detail) => Boolean(detail.marker)),
    filter((detail) => detail.marker.Type === MarkerType.PlanUnit.toString()),
    distinctUntilChanged(),
  )
  .subscribe(triggerPlanSummaryCardCallToActionClickEvent);

/**
 * Stream used to check when a spec summary card call to action is clicked.
 */
summaryCardCtaClicked$
  .pipe(
    filter((detail) => Boolean(detail.marker)),
    filter((detail) => detail.marker.Type === MarkerType.SpecUnit.toString()),
    distinctUntilChanged(),
  )
  .subscribe(triggerSpecSummaryCardCallToActionClickEvent);

/**
 * Triggers the carousel scrolled event for community markers.
 *
 * @param {Object} data - The data object containing carousel scroll information.
 */
function triggerCommunityCarouselScrolledEvent(data) {
  const marker = data.activeItem;

  const isNoResults = isNoResultsPage();

  const customProperties = {
    market_id: marker.MarketId,
    community_name: marker.Name,
    type: marker.Type,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    min_price: marker.PriceLow,
    max_price: marker.PriceHigh,
    community_status: marker.MarketingStatus?.Name,
    trustbuilder_reviews: marker.BuilderRating > 0,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_paid: !marker.IsBasic,
    is_zero_results: isNoResults,
    community_id: marker.Id,
    zip_code: marker.Zip,
    price_reduction: marker.PriceReductionLabel,
    direction: data.direction,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'carousel_card_swiped', customProperties })));
}

/**
 * Triggers the carousel scrolled event for plan markers.
 *
 * @param {Object} data - The data object containing carousel scroll information.
 */
function triggerPlanCarouselScrolledEvent(data) {
  const marker = data.activeItem;

  const isNoResults = isNoResultsPage();

  const customProperties = {
    market_id: marker.MarketId,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    type: marker.Type,
    min_price: marker.PriceLow,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    trustbuilder_reviews: marker.BuilderRating > 0,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_paid: !marker.IsBasic,
    is_zero_results: isNoResults,
    zip_code: marker.Zip,
    price_reduction: marker.PriceReductionLabel,
    plan_id: marker.Id,
    is_plan: true,
    is_spec: false,
    home_status: marker.StatusLabel,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'carousel_card_swiped', customProperties })));
}

/**
 * Triggers the carousel scrolled event for spec markers.
 *
 * @param {Object} data - The data object containing carousel scroll information.
 */
function triggerSpecCarouselScrolledEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    type: marker.Type,
    min_price: marker.PriceLow,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    trustbuilder_reviews: marker.BuilderRating > 0,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_paid: !marker.IsBasic,
    is_zero_results: isNoResultsPage(),
    zip_code: marker.Zip,
    price_reduction: marker.PriceReductionLabel,
    spec_id: marker.Id,
    is_plan: false,
    is_spec: true,
    home_status: marker.StatusLabel,
    current_card_position: data.currentCardPosition,
    next_card_position: data.nextCardPosition,
    listing_id: data.listingId,
    direction: data.direction,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'carousel_card_swiped', customProperties })));
}

summaryCardsCarouselScrolled$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.PlanUnit.toString()),
  )
  .subscribe(triggerPlanCarouselScrolledEvent);

summaryCardsCarouselScrolled$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.SpecUnit.toString()),
  )
  .subscribe(triggerSpecCarouselScrolledEvent);

summaryCardsCarouselScrolled$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.HousingDevelopment.toString()),
  )
  .subscribe(triggerCommunityCarouselScrolledEvent);

/**
 * Triggers the carousel closed event for community markers.
 * @param {Object} data - The data object containing the active marker.
 */
function triggerCommunityCarouselClosedEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    community_name: marker.Name,
    type: marker.Type,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    min_price: marker.PriceLow,
    max_price: marker.PriceHigh,
    community_status: marker.MarketingStatus?.Name,
    trustbuilder_reviews: marker.BuilderRating > 0,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_paid: !marker.IsBasic,
    community_id: marker.Id,
    zip_code: marker.Zip,
    price_reduction: marker.PriceReductionLabel,
    listing_id: data.listingId,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'carousel_closed', customProperties })));
}

/**
 * Triggers the carousel closed event for plan markers.
 * @param {Object} data - The data object containing the active marker.
 */
function triggerPlanCarouselClosedEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    type: marker.Type,
    min_price: marker.PriceLow,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    trustbuilder_reviews: marker.BuilderRating > 0,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_paid: !marker.IsBasic,
    zip_code: marker.Zip,
    price_reduction: marker.PriceReductionLabel,
    plan_id: marker.Id,
    is_plan: true,
    is_spec: false,
    home_status: marker.StatusLabel,
    listing_id: data.listingId,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'carousel_closed', customProperties })));
}

/**
 * Triggers the carousel closed event for spec markers.
 * @param {Object} data - The data object containing the active marker.
 */
function triggerSpecCarouselClosedEvent(data) {
  const marker = data.activeItem;

  const customProperties = {
    market_id: marker.MarketId,
    community_id: marker.CommunityId,
    community_name: marker.CommunityName,
    type: marker.Type,
    min_price: marker.PriceLow,
    builder_id: marker.BuilderId,
    builder_name: marker.BrandName,
    trustbuilder_reviews: marker.BuilderRating > 0,
    is_priced: Boolean(marker.PriceLow) && marker.PriceLow !== '0',
    is_paid: !marker.IsBasic,
    zip_code: marker.Zip,
    price_reduction: marker.PriceReductionLabel,
    spec_id: marker.Id,
    is_plan: false,
    is_spec: true,
    home_status: marker.StatusLabel,
    listing_id: data.listingId,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'carousel_closed', customProperties })));
}

/*
 * Triggers the saved search clicked event.
 */
const triggerSavedSearchClickedEvent = () => {
  const customProperties = {
    is_zero_results: isNoResultsPage(),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'save_search_clicked', customProperties })));
};

/*
 * Triggers the saved search modal shown event.
 */
const triggerSavedSearchModalShown = (modal) => {
  const customProperties = {
    is_zero_results: isNoResultsPage(),
    search_value: modal.variation,
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'save_search_modal_viewed', customProperties })));
};

/**
 * Triggers the saved search clicked event when the keep searching button is clicked.
 */
const triggerSavedSearchKeepSearchingEvent = (target) => {
  const customProperties = {
    is_zero_results: isNoResultsPage(),
    search_value: target.getAttribute('data-saved-search-name'),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'keep_searching_clicked', customProperties })));
};

/**
 * Triggers the saved search clicked event when the dashboard link is clicked.
 */
const triggerSavedSearchDashboardEvent = (target) => {
  const customProperties = {
    is_zero_results: isNoResultsPage(),
    search_value: target.getAttribute('data-saved-search-name'),
  };

  setTimeout(() => store.dispatch(googleAnalytics4Event({ eventName: 'saved_search_link_clicked', customProperties })));
};

carouselCloseButtonClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.HousingDevelopment.toString()),
  )
  .subscribe(triggerCommunityCarouselClosedEvent);

carouselCloseButtonClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.PlanUnit.toString()),
  )
  .subscribe(triggerPlanCarouselClosedEvent);

carouselCloseButtonClicked$
  .pipe(
    filter((marker) => Boolean(marker)),
    filter((marker) => marker.type === MarkerType.SpecUnit.toString()),
  )
  .subscribe(triggerSpecCarouselClosedEvent);

/*
  Triggers the saved search clicked event when the saved search button is clicked.
*/
onSavedSearchClicked$.subscribe(triggerSavedSearchClickedEvent);

/*
  Triggers the saved search modal shown.
*/
createdSaveSearchModalShown$.subscribe(triggerSavedSearchModalShown);

/**
 * Triggers the saved search clicked event when the saved search button is clicked.
 */
onKeepSearchingButtonClicked$.subscribe(triggerSavedSearchKeepSearchingEvent);

/**
 * Triggers the saved search clicked event when the saved search button is clicked.
 */
onDashboardLinkClicked$.subscribe(triggerSavedSearchDashboardEvent);
